// ============================================================================
// LE TABLEAU DE BORD — la porte d'entrée quotidienne, et l'écran sur lequel un commerçant DÉCIDE.
//
// ⚠️ TOUT CE QUI S'AFFICHE ICI EST MESURÉ. Les indicateurs viennent de `@orgazone/core-analytics`, le
// MÊME cœur que la caisse applique à sa journée — c'est ce qui fait qu'un vendeur et son responsable
// lisent le même chiffre. L'écran ne calcule rien, il met en forme.
//
// ⚠️ CE QUI N'EST PAS MESURABLE N'EST PAS DESSINÉ (§23.0). La maquette prévoit des cartes Conversion,
// Fidélité, Écarts, Campagnes et Météo : aucune n'a de source aujourd'hui — il n'existe pas de compteur
// de visiteurs, et la météo demande un service externe. Elles sont NOMMÉES dans un bloc qui dit ce qui
// leur manque, plutôt que remplies de valeurs plausibles. Sur l'écran où l'on décide d'embaucher ou de
// commander, un chiffre inventé coûte plus cher qu'une carte absente.
//
// ⚠️ UNE PANNE NE SE DÉGUISE PAS EN JOURNÉE CREUSE. Un échec de lecture affiche sa cause ; il n'affiche
// jamais des totaux à zéro.
// ============================================================================
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DASHBOARD_FIGURES, PAGE_PAD, noPhotoHatchCss, pageBleedMargin, type IconName } from '@orgazone/ui-tokens';
// ⚠️ Les paliers de lecture des indicateurs sont une règle MÉTIER : ils viennent du cœur.
import { CONVERSION_RATE_BANDS, IDENTIFIED_SALES_BANDS } from '@orgazone/core-analytics';
import type { ReactNode } from 'react';
import {
  Badge, Bars, Button, DateRangePicker, Donut, EmptyState, foldTail, Gauge, Icon, SegmentedThumb, showToast,
  SideDrawer, SeriesLegend, Sparkline, rangeLabel, Trans, WeatherPill,
  type DateRange, type DateRangeShortcut,
} from '@orgazone/ui-web';
// ⚠️ `BadgeTone` vient des JETONS, pas du rendu : c'est la charte qui possède la liste des tons.
import { donutVar, type BadgeTone } from '@orgazone/ui-tokens';
import { formatMoney } from '@orgazone/money-format';
// ⚠️ `dateCompact` VIENT DU PAQUET PARTAGÉ : l'écran Marketing s'en était écrit une copie locale
//    (`dateCourte`), et une quatrième définition de « jj/mm/aa » finirait par en dater une autre.
import { dateCompact, dateWithWeekdayLong, timeAgo } from '@orgazone/date-format';
import { greetingFromDisplayName } from '@orgazone/name-format';
import { api, type Campaign, type CampaignAttribution, type DashboardView, type EcartRow, type MissingCostsParams, type SalesMarginGap, type TablePrefs, type WeatherView } from '../api';
import { MissingCostNotice } from './marge-manquante';
import { phraseEcart } from './campaign-effect';
import { useAuth } from '../auth';
import { useStoreScope } from '../store-context';
import { fieldLabel } from '../vocabulary';
import { bornesDe, instantFiscal, isoDe, jourYmd, libellePeriode, useAncreDuJour, useHorlogeMinute, ymdDe, type PeriodeId } from '../periode';
import { pctBp, rescale } from '@orgazone/core-money';
import { key, t, type Translatable } from '../i18n';
import { currentLang } from '../lang';
import { channelLabel } from '../campaign-channels';

/**
 * LES QUATRE CRANS, comme la maquette — et QUATRE est un plafond, pas un choix de mise en page :
 * au-delà, un segmenté casse la largeur de sa piste avant de casser le sens (`SegmentedProps`). Tout
 * le reste (hier, 7 jours glissants, mois dernier…) est offert par le sélecteur de plage à côté, qui
 * existe précisément pour ça.
 *
 * ⚠️ « ANNÉE » ET NON « EXERCICE » comme la maquette : aucun début d'exercice n'est configurable dans
 * le schéma. Nommer « Exercice » une plage qui part du 1ᵉʳ janvier serait faux pour tout commerce dont
 * l'exercice commence ailleurs (§23.0) — l'étiquette suivra le jour où le réglage existera.
 * ⚠️ PAS DE « TOUT L'HISTORIQUE », contrairement au journal : cet écran compare à la MÊME période un
 * an plus tôt, et un historique sans début n'a pas d'équivalent à opposer.
 */
const CRANS: { id: PeriodeId; label: Translatable }[] = [
  { id: 'today', label: key('Jour') },
  // ⚠️ « SEMAINE » = LES 7 DERNIERS JOURS, pas la semaine calendaire (décision utilisateur 2026-08-19).
  // Une semaine en cours GRANDIT d'un jour chaque jour : le total du mardi porte deux journées, celui
  // du samedi six, et rien n'est comparable d'un jour à l'autre — ni au même cran la veille, ni à la
  // même période l'an dernier. Une fenêtre glissante pèse toujours sept jours.
  // ⚠️ La semaine calendaire reste offerte par le sélecteur (« Cette semaine ») : la question « où en
  // est ma semaine ? » existe aussi, elle n'a simplement pas sa place sur un cran de comparaison.
  { id: '7d', label: key('Semaine') },
  { id: 'month', label: key('Mois') },
  { id: 'year', label: key('Année') },
];

/**
 * LES PLAGES PROPOSÉES D'UN CLIC dans le sélecteur.
 * ⚠️ Elles réutilisent `bornesDe` — le sélecteur partagé ne calcule aucune date, exprès : une seconde
 * définition de « 7 derniers jours » finirait par désigner une autre plage que le cran du même nom.
 * ⚠️ « Tout l'historique » de la maquette est ÉCARTÉ : il n'a pas de début connu sans interroger la
 * base, et surtout il n'a pas d'équivalent N-1 à opposer — la carte maîtresse comparerait à rien.
 */
const RACCOURCIS: PeriodeId[] = ['today', 'yesterday', '7d', 'week', 'month', 'lastMonth', '14d', 'lastYear'];

/**
 * LES CARTES QUE L'ON PEUT MASQUER, dans l'ordre où le PANNEAU les liste (celui de la maquette).
 * ⚠️ Ce n'est pas l'ordre de l'écran : la maquette place « Météo du jour » en tête de son panneau et
 * la carte elle-même APRÈS le chiffre d'affaires. On suit les deux.
 *
 * ⚠️ LES IDENTIFIANTS SONT CEUX DE LA MAQUETTE (`chart`, `spark`, `pay`, `vend`, `top`) : le réglage
 * est enregistré sous ces clés, et le jour où les cartes manquantes arriveront, ce qu'un utilisateur
 * avait masqué voudra toujours dire la même chose.
 * ⚠️ LA MAQUETTE EN LISTE DIX, ET LES DIX SONT LÀ, plus `gaps` qui n'est pas d'elle.
 * ⚠️ CE COMMENTAIRE A ÉTÉ FAUX PENDANT DEUX JOURS, et c'est instructif : il annonçait « seules les
 * CAMPAGNES manquent » alors que la tuile avait été construite le 2026-09-05 et lit de vraies
 * campagnes (`Campagnes`, plus bas). Le bloc « pas encore mesuré » avait été mis à jour dans la même
 * passe, celui-ci non — deux endroits décrivant le même fait, un seul corrigé (Forme A). Un
 * commentaire périmé au SOMMET d'un fichier est le plus coûteux : c'est le premier lu, et il oriente
 * tout le reste de la lecture.
 * ⚠️ CE QUI RESTE VRAIMENT NON MESURÉ N'EST PLUS UNE CARTE MAIS DEUX GRANDEURS, et elles sont
 * déclarées à l'écran (`NotYetMeasured`) : l'EFFET d'une campagne (ni pixel d'ouverture, ni
 * rattachement d'un coupon à une vente, ni groupe témoin) et le TAUX DE TRANSFORMATION, qui attend un
 * compteur de passages — cadrage ouvert, arbitrage bloquant. Les deux cartes sont dessinées et DISENT
 * ce qui leur manque : les retirer ferait croire que l'indicateur n'est pas au programme.
 * ⚠️ TOUT PEUT ÊTRE MASQUÉ, y compris le chiffre d'affaires : c'est le choix de la maquette, et elle
 * assume l'écran vide en le NOMMANT (`ToutMasque`) au lieu de le laisser passer pour une panne.
 */
const CARTES = [
  { id: 'meteo', label: key('Météo du jour') },
  { id: 'chart', label: key('Chiffre d’affaires vs N-1') },
  { id: 'spark', label: key('Indicateurs & tendances') },
  { id: 'conv', label: key('Taux de transformation') },
  { id: 'ecarts', label: key('Écarts à traiter') },
  // ⚠️ VOISINE DES ÉCARTS DANS LA MAQUETTE, et ce voisinage a un sens : ce sont les deux tuiles qui
  //    appellent une ACTION, par opposition aux tuiles de constat au-dessus.
  { id: 'camp', label: key('Campagnes') },
  { id: 'pay', label: key('Moyens de paiement') },
  { id: 'vend', label: key('Ventes par vendeur') },
  { id: 'top', label: key('Top articles') },
  { id: 'fid', label: key('Fidélité') },
  { id: 'gaps', label: key('Ce qui n’est pas encore mesuré') },
] as const;

/** L'identifiant sous lequel les réglages de cet écran vivent, dans le magasin de préférences du compte. */
const PREFS_ID = 'dashboard';

const euro = (cents: number) => formatMoney(cents, { thousandsSep: ' ' });
/**
 * Montant SANS centimes — pour le cœur d'un anneau.
 * ⚠️ « 24 918,00 € » débordait de l'anneau de 148 px et venait chevaucher le tracé ; la maquette
 * appelle son formateur avec zéro décimale à cet endroit précis. Les centimes n'apportent rien sur un
 * cumul de période, et ils coûtent quatre caractères là où la place manque.
 * ⚠️ L'ARRONDI EST DÉLÉGUÉ au formateur (`displayScale`), jamais fait à la main ici : `money-format`
 * le confie à `rescale`, et un arrondi maison à côté finirait par diverger du reste de la plateforme.
 */
const euroRond = (cents: number) => formatMoney(cents, { thousandsSep: ' ', displayScale: 0 });
/** Un ratio en points de base rendu en pourcentage lisible (2 512 → « 25,1 % »). */
const pourcent = (bp: number, decimales = 1) => `${(bp / 100).toFixed(decimales).replace('.', ',')} %`;
/** Un entier avec ses séparateurs de milliers — l'idiome déjà employé par le reste du fichier. */
const nb = (v: number) => v.toLocaleString('fr-FR');
/** Une croissance porte TOUJOURS son signe : « +12,4 % » et « −3,1 % » ne se lisent pas pareil. */
const croissance = (bp: number) => `${bp >= 0 ? '+' : '−'}${(Math.abs(bp) / 100).toFixed(1).replace('.', ',')} %`;

type Statut =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'reloading' }
  | { phase: 'ready' }
  | { phase: 'failed'; message: string };

export function Dashboard() {
  const scope = useStoreScope();
  const { user } = useAuth();
  const gln = scope.view?.active?.gln ?? null;
  const ancre = useAncreDuJour();
  const [period, setPeriode] = useState<PeriodeId>('today');
  // ⚠️ UNE PLAGE LIBRE ÉTEINT LES CRANS au lieu d'en désigner un : `Segmented` accepte `null`, et un
  // cran allumé pendant qu'on regarde d'autres bornes affirmerait un réglage qui n'est pas appliqué.
  const [plage, setPlage] = useState<DateRange | null>(null);
  const [data, setData] = useState<DashboardView | null>(null);
  const [statut, setStatut] = useState<Statut>({ phase: 'idle' });
  /** A-t-on déjà montré quelque chose ? Sinon un rechargement viderait l'écran au lieu de le rafraîchir. */
  const displays = useRef(false);
  const [hidden, setMasquees] = useState<string[]>([]);
  const [panneau, setPanneau] = useState(false);

  // ⚠️ RÉGLAGE DE COMPTE, PAS D'APPAREIL : il vit dans le magasin de préférences du back-office (portée
  // PERSONNE, sans GLN) et suit donc l'utilisateur d'un poste à l'autre. Un `localStorage` ferait mentir
  // la phrase « réglage propre à votre compte » dès la deuxième machine.
  useEffect(() => {
    let vivant = true;
    api.tablePrefs(PREFS_ID)
      .then((p: TablePrefs) => { if (vivant) setMasquees(p.layout?.hidden ?? []); })
      // ⚠️ Un réglage illisible n'empêche PAS de travailler : on montre tout, c'est le défaut. Refuser
      // d'afficher le tableau de bord parce qu'une préférence n'a pas pu être lue serait disproportionné.
      .catch(() => { if (vivant) setMasquees([]); });
    return () => { vivant = false; };
  }, []);

  const basculer = (id: string) => {
    const suite = hidden.includes(id) ? hidden.filter((x) => x !== id) : [...hidden, id];
    setMasquees(suite);
    // ⚠️ ÉCRITURE OPTIMISTE ASSUMÉE, et c'est un cas où elle se défend : la bascule doit être
    // instantanée pour qu'on voie l'effet du réglage derrière le tiroir.
    // ⚠️ MAIS L'ÉCHEC SE DIT (§23.0) : sans ce message, on referme le panneau en croyant le réglage
    // gardé, et on le retrouve défait au prochain poste sans jamais savoir pourquoi.
    void api.saveTablePrefs(PREFS_ID, { layout: { order: CARTES.map((c) => c.id), hidden: suite } })
      .catch((e: unknown) => showToast(
        t('Réglage non enregistré : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err'));
  };

  const bornes = useMemo(
    () => (plage ? { from: ymdDe(plage.from), to: ymdDe(plage.to) } : bornesDe(period, ancre)),
    [plage, period, ancre],
  );

  const raccourcis = useMemo<DateRangeShortcut[]>(() => RACCOURCIS.flatMap((id) => {
    const b = bornesDe(id, ancre);
    return b.from && b.to ? [{ id, label: libellePeriode(id), from: isoDe(b.from), to: isoDe(b.to) }] : [];
  }), [ancre]);

  /** Ce que la carte maîtresse annonce comme période lue — le cran, ou la plage posée. */
  const periodHeading = plage ? rangeLabel(plage, raccourcis, '') : libellePeriode(period);

  useEffect(() => {
    // ⚠️ AUCUNE REQUÊTE SANS MAGASIN : `idle` dit « rien n'est parti », ce qui n'est pas « chargement ».
    if (!gln || !bornes.from || !bornes.to) { setStatut({ phase: 'idle' }); setData(null); return; }
    let vivant = true;
    setStatut(displays.current ? { phase: 'reloading' } : { phase: 'loading' });
    api.dashboard({ from: bornes.from, to: bornes.to })
      .then((vue) => {
        if (!vivant) return;
        displays.current = true;
        setData(vue);
        setStatut({ phase: 'ready' });
      })
      .catch((e: unknown) => {
        if (!vivant) return;
        // ⚠️ La donnée précédente est JETÉE : garder à l'écran les chiffres d'une période qu'on vient de
        // quitter serait pire que de ne rien montrer.
        setData(null);
        setStatut({ phase: 'failed', message: e instanceof Error ? e.message : String(e) });
      });
    return () => { vivant = false; };
  }, [gln, bornes.from, bornes.to]);

  return (
    <>
      <Hero
        magasin={data?.storeName ?? scope.view?.active?.name ?? ''}
        nom={user?.name ?? ''}
        day={ancre}
        activite={data?.activity ?? null}
        period={plage ? null : period}
        onPeriode={(p) => { setPlage(null); setPeriode(p); }}
        plage={plage}
        onPlage={setPlage}
        raccourcis={raccourcis}
        today={isoDe(jourYmd(ancre))}
        annees={data ? { current: data.period.from.slice(0, 4), precedent: data.previousPeriod.from.slice(0, 4) } : null}
        onPersonnaliser={() => setPanneau(true)}
      />

      {!gln ? <SansMagasin scope={scope} /> : null}

      {gln && statut.phase === 'failed' ? (
        <div className="dcard" role="alert" style={{ padding: 24 }}>
          <div style={{ fontSize: 'var(--t-h4)', fontWeight: 600, color: 'var(--danger-ink)', marginBottom: 6 }}>
            
            {t('Indicateurs illisibles')}
          </div>
          <p style={{ margin: 0, color: 'var(--ink-500)', fontSize: 'var(--t-body)', lineHeight: 1.6 }}>
            {t('La lecture n’a pas abouti : {message}. Aucun chiffre n’est affiché — ce n’est pas une journée sans vente, c’est une mesure qui n’a pas pu être faite.', { message: statut.message })}</p>
        </div>
      ) : null}

      {gln && !data && statut.phase === 'loading' ? (
        <div className="dcard"><EmptyState icon="grid" title={t('Mesure en cours…')} note={t('Les indicateurs sont calculés sur les pièces fiscales du magasin.')} /></div>
      ) : null}

      {data ? <Contenu vue={data} periodHeading={periodHeading} hidden={hidden} onPersonnaliser={() => setPanneau(true)} /> : null}


      <Personnaliser
        ouvert={panneau}
        hidden={hidden}
        onBasculer={basculer}
        onClose={() => setPanneau(false)}
      />
    </>
  );
}

/**
 * L'ACCROCHE — à qui on parle, de quel magasin, quel jour, et ce qui s'y passe.
 *
 * ⚠️ LE BANDEAU TEINTÉ DÉBORDE LA MARGE DE LA PAGE (`margin` négatif = rembourrage du `<main>`) : dans
 * la maquette il touche les bords, et un bandeau qui s'arrête à 24 px du bord se lit comme une carte
 * de plus au lieu de la coiffe de l'écran.
 */
function Hero({ magasin, nom, day, activite, period, onPeriode, plage, onPlage, raccourcis, today, annees, onPersonnaliser }: {
  /**
   * ⚠️ LE NOM DU MAGASIN, PAS LA RAISON SOCIALE. Le contexte de session porte le nom du CLIENT (celui
   * qu'on facture) ; l'enseigne du point de vente vit dans ses réglages. Coiffer l'écran de la raison
   * sociale donne « SARL Machin » là où l'exploitant attend « Boutique Centre » — et sur un client
   * multi-magasins, deux tableaux de bord différents s'annoncent sous le même titre.
   */
  magasin: string;
  nom: string;
  day: Date;
  activite: DashboardView['activity'] | null;
  /** ⚠️ `null` quand une plage libre pilote l'écran : aucun cran ne doit alors s'allumer. */
  period: PeriodeId | null;
  onPeriode: (p: PeriodeId) => void;
  plage: DateRange | null;
  onPlage: (v: DateRange | null) => void;
  raccourcis: DateRangeShortcut[];
  today: string;
  /** Les deux années comparées. ⚠️ `null` tant que rien n'est mesuré : on ne nomme pas une comparaison
   *  qui n'a pas encore eu lieu. */
  annees: { current: string; precedent: string } | null;
  onPersonnaliser: () => void;
}) {
  const now = useHorlogeMinute();
  const activiteTexte = phraseActivite(activite, now);
  const prenom = greetingFromDisplayName(nom);
  return (
    /* ⚠️ LE DÉBORDEMENT SE CALCULE, IL NE S'ÉCRIT PAS. Cette marge doit être l'exact opposé du
       rembourrage de la page pour que le bandeau touche l'en-tête et les deux bords. Écrite en dur
       (`-20px -24px`), elle a cessé d'accorder le jour où le rembourrage est passé à 24/28 : 4 px de
       fond apparaissaient au-dessus du bandeau, et tout le contenu descendait d'autant. */
    <header style={{
      margin: pageBleedMargin(22), padding: `26px ${PAGE_PAD.side}px 22px`,
      background: 'var(--hero)', borderBottom: '1px solid var(--hero-line)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18, flexWrap: 'wrap' }}>
        {/* ⚠️ `min(100%, …)` sur chaque largeur minimale de l'accueil : plus étroit que la colonne, l'écran la ramène à
            sa largeur au lieu de déborder sur le côté (un téléphone, une fenêtre réduite). */}
        <div style={{ flex: 1, minWidth: 'min(100%, 300px)' }}>
          <p style={{
            margin: '0 0 6px', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)', letterSpacing: '.09em',
            textTransform: 'uppercase', color: 'var(--ink-500)',
          }}>
            {magasin ? `${magasin} · ` : ''}{dateWithWeekdayLong(day)}
          </p>
          {/* ⚠️ LE PRÉNOM SEUL, comme la maquette (décision utilisateur 2026-08-18). La session ne
              porte qu'une chaîne « nom » : le premier mot en est extrait par `greetingFromDisplayName`,
              fonction d'AFFICHAGE dédiée — et non par `splitLegacyName`, qui découpe pour ÉCRIRE et
              n'a sa place que dans une migration. La convention « prénom d'abord » vient du formulaire
              d'inscription, dont l'exemple est « Céline Vireau » ; un compte saisi à l'envers sera donc
              salué par son nom de famille. Sans nom du tout, l'écran s'annonce par son titre. */}
          <h1 style={{
            margin: 0, fontWeight: 800, color: 'var(--ink-900)',
            fontSize: DASHBOARD_FIGURES.greetingSize, letterSpacing: DASHBOARD_FIGURES.greetingTracking,
          }}>
            {prenom ? t('Bonjour {prenom}', { prenom }) : t('Tableau de bord')}
          </h1>
          {activiteTexte ? (
            <p style={{ margin: '5px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-600)' }}>{activiteTexte}</p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onPersonnaliser}
          aria-label={t('Personnaliser les cartes')}
          style={{
            height: 40, padding: '0 15px', flexShrink: 0,
            border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)',
            background: 'var(--paper)', color: 'var(--ink-800)', fontSize: 'var(--t-title)', fontWeight: 600,
            display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer',
            boxShadow: '0 1px 2px rgba(15,20,25,.04)',
          }}
        >
          <Icon name="sliders" size={16} stroke={1.75} />
          
          {t('Personnaliser')}
        </button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 20, flexWrap: 'wrap' }}>
        {/* ⚠️ PISTE À POUCE GLISSANT, comme la maquette (corrigé 2026-09-05). Elle rendait un segmenté
            à pastille : la sélection sautait d'une case à l'autre au lieu de s'y déplacer, et
            l'actif portait une encre de marque là où la maquette garde la même encre partout.
            ⚠️ ET C'EST LA SEULE FORME QUI SAIT NE DÉSIGNER PERSONNE : `period` vaut `null` dès que
            les bornes viennent du sélecteur de dates, et le pouce s'efface. Une pastille resterait
            allumée sur un cran qui n'est plus en vigueur (§23.0). */}
        <SegmentedThumb options={CRANS.map((c) => ({ id: c.id, label: t(c.label) }))} value={period} onChange={onPeriode} label={t('Période affichée')} />
        <DateRangePicker
          value={plage}
          onChange={onPlage}
          shortcuts={raccourcis}
          // ⚠️ La borne haute est AUJOURD'HUI : demander demain rendrait « aucune vente », et cette
          // réponse-là se confond avec un magasin qui n'a rien vendu.
          max={today}
          idleHint={t('Les crans Jour / Semaine / Mois / Année pilotent l’écran')}
          clearLabel={t('Revenir aux crans')}
        />
        {!plage ? (
          <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-600)' }}>{t('Tout l’écran suit cette période')}</span>
        ) : null}
        {/* ⚠️ LA LÉGENDE EST AU BOUT DE LA LIGNE, poussée à droite : la maquette la place là et non
            dans la carte du chiffre d'affaires. Elle nomme ce que TOUT l'écran compare, et la carte
            gagne la hauteur qu'une légende interne lui aurait prise. */}
        <div style={{ flex: 1 }} />
        {annees ? <SeriesLegend current={annees.current} previous={annees.precedent} /> : null}
      </div>
    </header>
  );
}

/**
 * CE QUI SE PASSE DANS LE MAGASIN, en une phrase — ou rien à dire.
 *
 * ⚠️ LE PARC D'ABORD, ET IL VIENT DU BATTEMENT DE SYNCHRO, pas des ventes. Une caisse ouverte qui n'a
 * encore rien vendu est bien en service, et les pièces fiscales ne le diraient jamais. Le détail des
 * quatre formulations est dans `phraseParc`.
 * ⚠️ La dernière vente n'est PAS bornée à la période : « il y a 4 min » parle de maintenant.
 */
export function phraseActivite(a: DashboardView['activity'] | null, now: Date): string | null {
  if (!a) return null;
  const parc = phraseParc(a.fleet);
  const toInstant = instantFiscal(a.lastSaleAt);
  const derniere = toInstant ? t('dernière vente {v}', { v: timeAgo(toInstant, now) }) : null;
  if (!derniere) return t('{parc} · aucune vente enregistrée dans ce magasin', { parc });
  return `${parc} · ${derniere}`;
}

/**
 * LE PARC, TOUJOURS DIT — dans les quatre situations, jamais par le silence.
 *
 * ⚠️ CE MEMBRE DE PHRASE NE DISPARAÎT JAMAIS : sans lui l'accroche se réduirait à « dernière vente il y
 * a 4 minutes », et un exploitant n'aurait aucun moyen de savoir si son parc va bien, s'il est vide, ou
 * si personne ne l'a mesuré. Le §23.0 interdit d'inventer un nombre ; il n'a jamais demandé de taire
 * la question.
 *
 * ⚠️ LES QUATRE CAS SE DISTINGUENT, parce qu'ils ne se réparent pas au même endroit : « non mesuré »
 * est une panne de lecture côté serveur, « aucune caisse rattachée » un magasin qui n'a jamais
 * synchronisé, et « 2 sur 3 » une caisse à aller rallumer.
 *
 * ⚠️ « EN SERVICE », le mot de la maquette — et le mot juste : une caisse qui donne son battement de
 * synchronisation est une caisse qui travaille.
 *
 * ⚠️ « SUR M » N'APPARAÎT QUE S'IL MANQUE QUELQU'UN : « 3 caisses sur 3 » est une précision qui
 * n'informe personne, quand « 2 caisses sur 3 » est exactement ce qu'on doit voir en ouvrant.
 *
 * ⚠️ UNE PHRASE COMPTÉE PAR SORTE DE MACHINE, JAMAIS UN NOM PASSÉ EN VALEUR : une clé commune
 * (`{online} {nom} en service`) recevait « caisse » ou « caisses » choisis à la main — deux formes pour
 * toutes les langues, et des mots que le catalogue ne voyait pas, donc jamais traduits.
 */
export function phraseParc(f: DashboardView['activity']['fleet']): string {
  if (!f) return t('parc de caisses non mesuré');
  const caisses = f.known === 0 ? t('aucune caisse rattachée à ce magasin')
    : f.online === f.known ? t('{n} caisse en service', { n: f.online })
      : t('{n} caisse sur {known} en service', { n: f.online, known: f.known });
  // ⚠️ LES TERMINAUX DE STOCK (Mobile Partner) SONT DES MACHINES DU PARC, ET ILS SE DISENT — à part des
  //    caisses, parce qu'ils n'en sont pas, et seulement s'il y en a : « 0 terminal » sur un magasin qui
  //    n'en a jamais eu affirmerait une absence que personne ne cherche.
  const terminals = f.terminals;
  if (!terminals || terminals.known === 0) return caisses;
  const terminaux = terminals.online === terminals.known
    ? t('{n} terminal de stock en service', { n: terminals.online })
    : t('{n} terminal de stock sur {known} en service', { n: terminals.online, known: terminals.known });
  return `${caisses} · ${terminaux}`;
}

/** Les quatre situations sans magasin, jamais confondues — reprises du journal, même contrat. */
function SansMagasin({ scope }: { scope: ReturnType<typeof useStoreScope> }) {
  if (scope.loading) {
    return <div className="dcard"><EmptyState icon="store" title={t('Lecture du contexte…')} note={t('Le back-office cherche quel magasin cette session administre.')} /></div>;
  }
  if (scope.error) {
    return (
      <div className="dcard" role="alert" style={{ padding: 24 }}>
        <div style={{ fontSize: 'var(--t-h4)', fontWeight: 600, color: 'var(--danger-ink)', marginBottom: 6 }}>{t('Contexte magasin illisible')}</div>
        <p style={{ margin: 0, color: 'var(--ink-500)', fontSize: 'var(--t-body)', lineHeight: 1.6 }}>
          {t('Le magasin de cette session n’a pas pu être lu : {error}. Aucun indicateur n’est affiché.', { error: scope.error })}</p>
      </div>
    );
  }
  const aucun = scope.view && scope.view.stores.length === 0;
  return (
    <div className="dcard">
      <EmptyState
        icon="store"
        title={aucun ? t('Aucun magasin ouvert à ce compte') : t('Aucun magasin actif')}
        note={aucun
          ? t('Aucun magasin n’est ouvert à ce compte : un administrateur doit accorder l’accès magasin par magasin.')
          : t('Choisissez le magasin à administrer dans la barre du haut. Les indicateurs sont ceux de CE magasin.')}
      />
    </div>
  );
}

function Contenu({ vue, periodHeading, hidden, onPersonnaliser }: {
  vue: DashboardView; periodHeading: string; hidden: string[]; onPersonnaliser: () => void;
  /** Ouvre le rapport des fiches qui faussent le taux de clients identifiés. */
}) {
  const { totals, previous, growth, chart } = vue;
  const points = chart.points.map((p) => ({
    key: p.key,
    value: p.caTtcCents,
    label: etiquetteSeau(p.key, chart.grain),
    compare: p.prevCaTtcCents,
  }));
  const anCourant = Number(vue.period.from.slice(0, 4));
  const anPrecedent = Number(vue.previousPeriod.from.slice(0, 4));
  const visible = (id: string) => !hidden.includes(id);
  const meteo = useMeteo();

  // ⚠️ Un écran vide se DIT. Sans ce cas, masquer la dernière carte laisse une page blanche que
  // l'utilisateur attribuera à une panne de mesure plutôt qu'à son propre réglage (§23.0).
  if (CARTES.every((c) => hidden.includes(c.id))) return <ToutMasque onPersonnaliser={onPersonnaliser} />;

  return (
    <>
      {/* ── La carte maîtresse : le chiffre, sa comparaison, sa courbe ── */}
      {visible('chart') ? (
      <div className="dcard" style={{ padding: '22px 24px', marginBottom: 14, display: 'flex', gap: 28, flexWrap: 'wrap' }}>
        <div style={{ width: 196, flexShrink: 0 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)', letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
            {periodHeading}
          </div>
          <div style={{
            fontWeight: 800, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.1, marginTop: 8, whiteSpace: 'nowrap',
            fontSize: DASHBOARD_FIGURES.heroAmountSize, letterSpacing: DASHBOARD_FIGURES.heroAmountTracking,
          }}>
            {/* ⚠️ SANS CENTIMES, COMME LA MAQUETTE — qui appelle son formateur avec zéro décimale
                (`eur(…, 0)`) pour ce montant ET pour sa comparaison. La passe du 2026-09-04 n'avait
                arrondi que les BARRES : les deux chiffres les plus gros de la carte gardaient leurs
                centimes, donc la correction ne se voyait pas là où on la cherchait. */}
            {euroRond(totals.caTtcCents)}
          </div>
          <Delta bp={growth.caBp} />
          <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 12, fontVariantNumeric: 'tabular-nums' }}>
            
            {/* ⚠️ LES DATES RÉELLEMENT COMPARÉES : N-1 recule de 364 jours (même jour de semaine), donc ses bornes tombent un
                ou deux jours après la même date de l'an dernier, et l'écran doit le montrer. */}
            {t('N-1, même jour de semaine')}<br />
            <span style={{ fontSize: 'var(--t-note)' }}>{rangeLabel({ from: isoDe(vue.previousPeriod.from), to: isoDe(vue.previousPeriod.to) }, [], '—')}</span><br />
            <span style={{ color: 'var(--ink-700)', fontWeight: 600 }}>{euroRond(previous.caTtcCents)}</span>
          </div>
          {/* ⚠️ LE GRAIN EST ÉCRIT, ET SUR LA MÊME LIGNE que le périmètre — c'est le `periodSub` de la
              maquette. Sans cette mention, une barre de six mois couvre un mois entier sans que rien
              ne le dise. Sur deux lignes, elle encombrait une colonne de 196 px ; la précision
              « ventes annulées exclues » part avec, elle est du ressort de la spec, pas de l'écran. */}
          <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-400)', marginTop: 14, lineHeight: 1.5 }}>
            {t('Chiffre d’affaires TTC, retours déduits · {v}', { v: grainLisible(chart.grain) })}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 'min(100%, 280px)' }}>
          {points.length > 0
            ? (
              // ⚠️ MONTANTS ARRONDIS À L'EURO, ET C'EST PUREMENT VISUEL (demande utilisateur
              //    2026-09-04) : sur une courbe, les centimes ajoutent deux chiffres à chaque
              //    étiquette sans rien apprendre — la carte des vendeurs le faisait déjà.
              // ⚠️ L'ARRONDI EST UN RENDU, JAMAIS UN CALCUL : les valeurs restent en centimes, et
              //    c'est `displayScale` du formateur partagé qui coupe l'affichage. Arrondir la
              //    donnée fausserait les totaux, et l'écart se verrait au premier rapprochement.
              <Bars
                points={points}
                format={euroRond}
                labelEvery={points.length > 16 ? 2 : 1}
                series={{ current: String(anCourant), previous: String(anPrecedent) }}
              />
            )
            : <EmptyState icon="grid" title={t('Aucune vente sur cette période')} note={t('Rien n’a été encaissé — ce n’est pas une panne de mesure.')} />}
        </div>
      </div>
      ) : null}

      {/* ── LA MÊME GRILLE POUR LA MÉTÉO ET LES KPI, comme la maquette : la météo n'est pas une bande
             pleine largeur, c'est UNE tuile parmi les autres. Le pas de 332 px est celui de la
             maquette (le 214 px que portait cette grille est celui de la variante DÉPÔT, un autre
             écran). Toutes les tuiles font `height:100%` : la grille les égalise, sinon la carte
             météo — plus courte — laisserait un trou sous elle. ── */}
      {visible('meteo') || visible('spark') || visible('conv') ? (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 332px), 1fr))', gap: 14, marginBottom: 14 }}>
        {visible('meteo') ? <Meteo vue={meteo.vue} failure={meteo.failure} /> : null}
        {visible('spark') ? (
          <>
            {/* ⚠️ UNE TEINTE PAR INDICATEUR, relevée sur la maquette : menthe pour les tickets, bleu
                pour le panier, violet pour l'indice de vente. La couleur suit l'INDICATEUR et ne
                tourne jamais — c'est ce qui permet de reconnaître une courbe sans lire son titre. */}
            <Tile heading={t('Tickets')} valeur={String(totals.nbSales)} deltaBp={growth.salesBp} serie={chart.points.map((p) => p.nbSales)} teinte="var(--teal-500)" />
            <Tile heading={t('Panier moyen')} valeur={euro(totals.panierMoyenCents)} deltaBp={growth.panierBp} serie={chart.points.map((p) => p.panierMoyenCents)} teinte="var(--blue)" />
            <Tile heading={t('Indice de vente')} valeur={(totals.idvX10 / 10).toFixed(1).replace('.', ',')} serie={chart.points.map((p) => p.idvX10)} note={t('articles par vente')} teinte="var(--violet)" />
            <ClientsIdentifies bp={totals.tauxClientsIdentifiesBp} identifiees={totals.nbSalesIdentifiees} total={totals.nbSales} />
          </>
        ) : null}
        {visible('conv') ? (
          <Transformation tickets={totals.nbSales} passages={vue.activity.footfall}
            dernierComptage={vue.activity.footfallLastAt} />
        ) : null}
      </div>
      ) : null}

      {/* ⚠️ DEUX CARTES SUR CETTE LIGNE, pas trois : la maquette réserve la rangée aux deux anneaux de
             répartition, dans une grille au pas de 340 px. « Top articles » vit dans SA rangée juste
             en dessous — glissé ici, il compressait les trois cartes sous le pas de l'anneau (148 px
             plus ses marges) et l'anneau se retrouvait à l'étroit. */}
      {visible('pay') || visible('vend') ? (
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
        gap: 14, marginBottom: 14, alignItems: 'start',
      }}>
        {/* ⚠️ LE VENDEUR AVANT LE PAIEMENT — l'ordre du dessin (`vVend` puis `vPay`), et il a un sens :
            on regarde d'abord QUI a vendu, ensuite COMMENT c'est encaissé. L'inverse plaçait un
            détail d'encaissement avant la performance de l'équipe. */}
        {visible('vend') ? (
          <CarteRepartition
            heading={t('Ventes par vendeur')}
            period={periodHeading}
            vide={t('Aucune vente attribuée sur la période.')}
            parts={foldTail(vue.sellers.map((x) => ({ key: x.seller, label: x.seller, value: x.caTtcCents, note: t('{n} ticket', { n: x.nbSales }) })), 6)}
            centre={{ valeur: euroRond(vue.sellers.reduce((sum, x) => sum + x.caTtcCents, 0)), note: t('{n} ticket', { n: totals.nbSales }) }}
          />
        ) : null}
        {visible('pay') ? (
          <CarteRepartition
            heading={t('Moyens de paiement')}
            period={periodHeading}
            vide={t('Aucune pièce encaissée sur la période.')}
            parts={foldTail(vue.payments.map((x) => ({ key: x.method, label: x.method, value: x.amountCents })), 6)}
            centre={vue.payments[0]
              ? { valeur: pourcent(vue.payments[0].shareBp, 0), note: t('en {v}', { v: vue.payments[0].method.toLowerCase() }) }
              : null}
          />
        ) : null}
      </div>
      ) : null}

      {/* ⚠️ « Top articles » a SA rangée, en flux souple : la maquette lui donne `flex: 2 1 460px`,
             c'est-à-dire deux fois la place d'un futur voisin, et une largeur de repli de 460 px. */}
      {/* ⚠️ MÊME RANGÉE QUE LA MAQUETTE : le classement prend deux parts (repli 460 px), la fidélité
             une seule (repli 300 px, plafond 400). Ce n'est pas décoratif — une carte de quatre
             chiffres à côté d'un tableau de huit lignes doit rester étroite, sinon ses valeurs
             flottent au milieu du vide. */}
      {visible('top') || (visible('fid') && vue.loyalty) ? (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-start', marginBottom: 14 }}>
        {visible('top') ? (
          <div style={{ flex: '2 1 460px', minWidth: 0 }}>
            <TopArticles rows={vue.topArticles} period={periodHeading} />
          </div>
        ) : null}
        {visible('fid') && vue.loyalty ? (
          <div style={{ flex: '1 1 300px', minWidth: 0, maxWidth: 400 }}>
            <LoyaltyCard totals={vue.loyalty} period={periodHeading} />
          </div>
        ) : null}
      </div>
      ) : null}

      {/* ⚠️ LA DERNIÈRE RANGÉE, ET ELLE N'APPARTIENT À PERSONNE D'AUTRE. La maquette ouvre ICI un
             flex à part — écarts et campagnes, chacun `1 1 430px` — et il est le seul SANS
             `margin-bottom` : c'est le bas de page. Les écarts avaient d'abord été posés parmi les
             KPI, puis dans la rangée du classement ; ni l'un ni l'autre n'est ce que montre la
             maquette.
         ⚠️ Et le voisinage porte du sens : parmi des tuiles qui CHANGENT AVEC LA PÉRIODE, le nombre
             d'écarts se lisait comme un chiffre du mois — alors qu'un écart n'est borné par aucune
             période. C'est déjà la raison de sa route à part (`/variances/resume`, jamais
             `/dashboard`) : mal placée, la tuile démentait ce que le serveur garantissait. */}
      {visible('ecarts') || visible('camp') ? (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-start' }}>
        {visible('ecarts') ? (
          <div style={{ flex: '1 1 430px', minWidth: 0 }}>
            <EcartsATraiter />
          </div>
        ) : null}
        {/* ⚠️ LES DEUX AU MÊME `1 1 430px`, DONC MOITIÉ-MOITIÉ — c'est le relevé de la maquette, et
            c'est aussi ce qui rend enfin sa largeur juste à la tuile des écarts : seule dans sa
            rangée, elle prenait toute la page alors qu'elle avait été DIMENSIONNÉE pour une moitié.
            Sa largeur n'était pas fausse en elle-même ; il lui manquait sa voisine. */}
        {visible('camp') ? (
          <div style={{ flex: '1 1 430px', minWidth: 0 }}>
            <Campagnes />
          </div>
        ) : null}
      </div>
      ) : null}

      {visible('gaps') ? <NotYetMeasured gap={totals.marginGap} query={{ from: vue.period.from, to: vue.period.to }} /> : null}

      {/* ⚠️ MENTION EXIGÉE PAR LA LICENCE du service météo, et elle doit rester VISIBLE — la reléguer
          dans une page légale ne satisfait pas la condition. Ce qui est exigé est sa VISIBILITÉ, pas
          sa place dans la tuile : posée en pied d'écran, elle est lue par qui la cherche sans encombrer
          une carte de 332 px. Elle n'apparaît que si une météo a réellement été servie, et son texte
          vient du serveur — écrit en dur, il survivrait à un changement de fournisseur. */}
      {visible('meteo') && meteo.vue?.ok ? (
        <div style={{ marginTop: 10, textAlign: 'right', fontSize: 'var(--t-sub)', color: 'var(--ink-400)' }}>
          {meteo.vue.attribution}
        </div>
      ) : null}

    </>
  );
}

/**
 * TOUTES LES CARTES MASQUÉES — l'état que la personnalisation rend possible, et qu'il faut nommer.
 * ⚠️ Il porte le chemin du retour : une page blanche sans bouton laisserait l'utilisateur devant un
 * écran qu'il a lui-même éteint, sans savoir où le rallumer.
 */
function ToutMasque({ onPersonnaliser }: { onPersonnaliser: () => void }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      textAlign: 'center', padding: '34px 20px', marginBottom: 14,
      border: '1px dashed var(--ink-300)', borderRadius: 'var(--r-md)', background: 'var(--paper)',
    }}>
      <span style={{
        width: 50, height: 50, borderRadius: 'var(--r-md)', marginBottom: 13,
        background: 'var(--teal-100)', color: 'var(--teal-700)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon name="grid" size={25} stroke={1.6} />
      </span>
      <span style={{ fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Toutes les cartes sont masquées')}</span>
      <p style={{ margin: '4px 0 0', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', maxWidth: 340, lineHeight: 1.55 }}>
        
        {t('Le tableau de bord n’affiche que les cartes que vous choisissez. Réactivez-en depuis « Personnaliser » — le réglage reste propre à votre compte.')}
      </p>
      <button
        type="button"
        onClick={onPersonnaliser}
        style={{
          marginTop: 12, height: 36, padding: '0 15px', border: 'none', cursor: 'pointer',
          borderRadius: 'var(--r-ctrl)', background: 'var(--ink-900)', color: 'var(--paper)',
          fontSize: 'var(--t-body)', fontWeight: 700,
        }}
      >
        
        {t('Choisir les cartes')}
      </button>
    </div>
  );
}

/**
 * LE TIROIR DE PERSONNALISATION — quelles cartes s'affichent, réglage par réglage.
 *
 * ⚠️ AUCUN BOUTON DE VALIDATION : chaque bascule est enregistrée au geste. Un « Valider » laisserait
 * croire que rien n'est pris en compte tant qu'on ne l'a pas cliqué, alors que l'écran derrière a
 * déjà changé — c'est d'ailleurs pour VOIR cet effet que le panneau est un tiroir et non une modale.
 */
function Personnaliser({ ouvert, hidden, onBasculer, onClose }: {
  ouvert: boolean; hidden: string[]; onBasculer: (id: string) => void; onClose: () => void;
}) {
  return (
    <SideDrawer
      open={ouvert}
      onClose={onClose}
      title={t('Personnaliser')}
      subtitle={t('Réglage propre à votre compte')}
      footNote={t('L’ordre d’affichage n’est pas encore réglable : les cartes se suivent dans l’ordre de cette liste.')}
    >
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)', letterSpacing: '.09em', textTransform: 'uppercase',
        color: 'var(--ink-500)', fontWeight: 600, marginBottom: 10,
      }}>
        
        {t('Cartes affichées')}
      </div>
      {CARTES.map((c) => {
        const displayed = !hidden.includes(c.id);
        return (
          <button
            key={c.id}
            type="button"
            role="switch"
            aria-checked={displayed}
            onClick={() => onBasculer(c.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '12px 0',
              border: 'none', borderBottom: '1px solid var(--ink-100)', background: 'transparent',
              textAlign: 'left', cursor: 'pointer',
            }}
          >
            <span style={{ flex: 1, fontSize: 'var(--t-title)', fontWeight: 500, color: 'var(--ink-900)' }}>{t(c.label)}</span>
            {/* ⚠️ La coche n'est PAS un simple aplat de couleur : une case cochée et une case vide
                doivent se distinguer autrement que par la teinte (contraste forcé, daltonisme). */}
            <span aria-hidden style={{
              width: 22, height: 22, borderRadius: 'var(--r-xs)', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: displayed ? 'var(--teal-500)' : 'var(--paper)',
              border: displayed ? 'none' : '1.5px solid var(--ink-300)',
              color: '#fff',
            }}>
              {displayed ? <Icon name="check" size={14} stroke={2.6} /> : null}
            </span>
          </button>
        );
      })}
    </SideDrawer>
  );
}

/** La pastille de croissance — verte à la hausse, ambre à la baisse, jamais muette. */
function Delta({ bp }: { bp: number }) {
  const hausse = bp >= 0;
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, height: 24, padding: '0 10px',
      borderRadius: 'var(--r-pill)', marginTop: 10, fontSize: 'var(--t-dense)', fontWeight: 700,
      background: hausse ? 'var(--teal-100)' : 'var(--amber-bg)',
      color: hausse ? 'var(--teal-800)' : 'var(--amber-fg)',
    }}>
      <Icon name={hausse ? 'chevronU' : 'chevronD'} size={13} />
      {croissance(bp)}
    </div>
  );
}

function Tile({ heading, valeur, deltaBp, serie, note, teinte }: {
  heading: string; valeur: string; deltaBp?: number; serie?: number[]; note?: string; teinte?: string;
}) {
  return (
    <div className="dcard" style={{ padding: 18, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)', letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>
        {heading}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 7 }}>
        <span style={{ fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.03em', color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {valeur}
        </span>
        {deltaBp !== undefined ? (
          <span style={{ fontSize: 'var(--t-dense)', fontWeight: 700, color: deltaBp >= 0 ? 'var(--teal-700)' : 'var(--amber-fg)', whiteSpace: 'nowrap' }}>
            {croissance(deltaBp)}
          </span>
        ) : null}
        {note ? <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-400)', whiteSpace: 'nowrap' }}>{note}</span> : null}
      </div>
      {/* La sparkline vient à fleur des bords de la carte — elle annule les 18 px de marge intérieure. */}
      {serie && serie.length > 1 ? <div style={{ margin: 'auto -18px -18px', paddingTop: 12 }}><Sparkline values={serie} stroke={teinte} /></div> : null}
    </div>
  );
}

/**
 * CARTE DE RÉPARTITION — un anneau et sa légende chiffrée.
 *
 * ⚠️ MÊME ANATOMIE POUR LES DEUX CARTES de la maquette (paiements, vendeurs) : en-tête à filet, anneau
 * de 148 centré sur son total, puis une ligne par part avec sa pastille. Les vendeurs étaient rendus
 * en simple CLASSEMENT — on lisait un ordre, jamais une répartition, et la question « qui pèse
 * combien ? » restait sans réponse visuelle.
 * ⚠️ L'ANNEAU ET SA LÉGENDE PARTAGENT L'ORDRE DES PARTS : la Nième ligne porte la Nième teinte. C'est
 * ce qui rend la légende utilisable — sans quoi il faudrait deviner quelle part est laquelle.
 */
function CarteRepartition({ heading, period, parts, centre, vide }: {
  heading: string;
  period: string;
  parts: Array<{ key: string; label: string; value: number; note?: string }>;
  /** Ce qui s'inscrit au centre de l'anneau. `null` = rien à y mettre. */
  centre: { valeur: string; note: string } | null;
  vide: string;
}) {
  const total = parts.reduce((sum, x) => sum + Math.max(0, x.value), 0);
  return (
    <div className="dcard" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px',
        borderBottom: '1px solid var(--ink-200)',
      }}>
        <span style={{ flex: 1, fontSize: 'var(--t-h4)', fontWeight: 700, letterSpacing: '-0.015em', color: 'var(--ink-900)' }}>
          {heading}
        </span>
        {/* ⚠️ La période est RAPPELÉE sur la carte : deux cartes côte à côte lisant des périodes
            différentes seraient indiscernables, et c'est arrivé assez souvent ailleurs pour que la
            maquette la répète ici. */}
        <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-400)', whiteSpace: 'nowrap' }}>{period}</span>
      </div>

      {parts.length === 0 ? (
        <EmptyState icon="grid" title={vide} note={t('Rien n’a été mesuré sur cette période — ce n’est pas un défaut de lecture.')} />
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '22px 20px 10px', position: 'relative' }}>
            <Donut slices={parts} format={euro}>
              {centre ? (
                <>
                  <span style={{
                    fontSize: 'var(--t-h2)', fontWeight: 800, letterSpacing: '-0.03em',
                    color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums',
                  }}>{centre.valeur}</span>
                  <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{centre.note}</span>
                </>
              ) : null}
            </Donut>
          </div>
          <div style={{ padding: '6px 12px 12px' }}>
            {parts.map((x, i) => (
              <div key={x.key} className="lgd" style={{
                display: 'flex', alignItems: 'center', gap: 11, padding: 8, borderRadius: 'var(--r-ctrl)',
              }}>
                <span aria-hidden style={{
                  width: 9, height: 9, borderRadius: 'var(--r-r3)', background: donutVar(i), flexShrink: 0,
                }} />
                <span style={{
                  flex: 1, minWidth: 0, fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-900)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{x.label}</span>
                {x.note ? (
                  <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {x.note}
                  </span>
                ) : null}
                <span style={{
                  width: 100, textAlign: 'right', fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-900)',
                  fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                }}>{euro(x.value)}</span>
                <span style={{
                  width: 38, textAlign: 'right', fontSize: 'var(--t-dense)', color: 'var(--ink-500)',
                  fontVariantNumeric: 'tabular-nums',
                }}>{total > 0 ? `${rescale(pctBp(Math.max(0, x.value), total), 2, 0)} %` : '—'}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * TEINTE STABLE D'UNE FAMILLE — la même partout, quelle que soit la période affichée.
 *
 * ⚠️ PAS UN INDEX DE RANG. Colorer par la position dans le classement ferait changer la couleur d'un
 * rayon d'une semaine à l'autre : « le vert, c'est quoi déjà ? » à chaque consultation. Une empreinte
 * du NOM donne la même teinte à « Pulls » aujourd'hui et le mois prochain.
 * ⚠️ Deux familles peuvent tomber sur la même teinte, et ce n'est pas grave : le nom est écrit juste
 * à côté de la pastille — la couleur aide à balayer, elle ne porte pas l'identité.
 */
function teinteFamille(nom: string): string {
  let h = 0;
  for (let i = 0; i < nom.length; i += 1) h = (h * 31 + nom.charCodeAt(i)) >>> 0;
  return donutVar(h % 6);
}

/**
 * LE CLASSEMENT DES ARTICLES — un vrai tableau, pas une liste.
 *
 * ⚠️ CINQ COLONNES DE LARGEUR FIXE relevées sur la maquette (34 · 1fr · 96 · 52 · 88) : sans elles,
 * les quantités et les montants ne s'alignent pas d'une ligne à l'autre, et un classement dont les
 * chiffres ne sont pas en colonne ne se compare pas — c'est pourtant sa seule raison d'être.
 * ⚠️ LA VIGNETTE EST UNE HACHURE, pas une image manquante : le catalogue ne porte pas de photo, et un
 * carré vide se lirait comme une image qui n'a pas chargé.
 * ⚠️ Ce n'est pas `DataTable` : celui-ci apporte tri, pagination et en-tête collant à une carte de
 * synthèse de huit lignes qui n'en veut aucun. La maquette dessine d'ailleurs une grille, pas un
 * tableau de données.
 */
function TopArticles({ rows, period }: {
  rows: DashboardView['topArticles'];
  period: string;
}) {
  // ⚠️ LA COLONNE FAMILLE S'ADAPTE AU PLUS LONG LIBELLÉ (`max-content`), là où la maquette la fige à
  // 96 px : ses familles de démonstration tiennent en un mot (« Pulls »), les vraies non — et le nom
  // tronqué venait alors coller la quantité. Plancher à 96 pour que la colonne ne s'effondre pas sur
  // un jeu de familles courtes, plafond posé sur le TEXTE (voir `maxWidth` plus bas) : sans lui, une
  // famille à rallonge repousserait le libellé de l'article, qui est l'information principale.
  const COLONNES = '34px minmax(110px, 1fr) minmax(96px, max-content) 52px 88px';
  return (
    <div className="dcard" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px',
        borderBottom: '1px solid var(--ink-200)',
      }}>
        <span style={{ flex: 1, fontSize: 'var(--t-h4)', fontWeight: 700, letterSpacing: '-0.015em', color: 'var(--ink-900)' }}>
          
          {t('Top articles')}
        </span>
        <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-400)', whiteSpace: 'nowrap' }}>{period}</span>
      </div>

      {rows.length === 0 ? (
        <EmptyState icon="grid" title={t('Aucun article vendu sur la période')} note={t('Rien n’a été mesuré — ce n’est pas un défaut de lecture.')} />
      ) : (
        <>
          <div style={{
            display: 'grid', gridTemplateColumns: COLONNES, gap: 8, padding: '9px 20px',
            background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)',
            fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em',
            textTransform: 'uppercase', color: 'var(--ink-500)',
          }}>
            <div />
            <div>{t('Article')}</div>
            <div>{fieldLabel('family')}</div>
            <div style={{ textAlign: 'right' }}>{t('Qté')}</div>
            <div style={{ textAlign: 'right' }}>CA</div>
          </div>

          {rows.map((a) => (
            <div
              key={a.gencode ?? a.label}
              style={{
                display: 'grid', gridTemplateColumns: COLONNES, gap: 8, padding: '0 20px',
                height: 'var(--row-h)', alignItems: 'center',
                borderBottom: '1px solid var(--ink-100)', fontSize: 'var(--t-title)',
              }}
            >
              <div aria-hidden style={{
                width: 28, height: 28, borderRadius: 'var(--r-xs)',
                background: noPhotoHatchCss(),
              }} />
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontWeight: 600, color: 'var(--ink-900)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{a.label}</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)', color: 'var(--ink-400)' }}>
                  {a.gencode ?? '—'}
                </div>
              </div>
              <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-600)', minWidth: 0 }}>
                {/* ⚠️ Sans famille au catalogue, on écrit un tiret — pas un « Divers » qui ferait
                    croire à un rayon qui n'existe pas. */}
                {a.family ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap', minWidth: 0 }}>
                    <span aria-hidden style={{
                      width: 7, height: 7, borderRadius: 'var(--r-r2)', background: teinteFamille(a.family), flexShrink: 0,
                    }} />
                    <span style={{ maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.family}</span>
                  </span>
                ) : <span style={{ color: 'var(--ink-400)' }}>—</span>}
              </div>
              <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--ink-600)' }}>
                {a.qty}
              </div>
              <div style={{
                textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600,
                color: 'var(--ink-900)', whiteSpace: 'nowrap',
              }}>{euro(a.caTtcCents)}</div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/**
 * LA FIDÉLITÉ DE LA PÉRIODE — ce qui a été acquis, ce qui a été consommé.
 *
 * ⚠️ LES TROIS SOMMES VIENNENT DU CŒUR `@orgazone/core-loyalty`, pas d'un calcul d'écran ni d'une
 * requête : la caisse et le futur module de statistiques comptent la fidélité avec la même définition.
 * L'écran ne fait que mettre en forme.
 * ⚠️ LA BARRE PEUT DÉBORDER DES 100 % — on consomme une cagnotte accumulée AVANT la période, et c'est
 * même le moment où le programme produit son effet. Elle est donc bornée à l'affichage, mais le
 * pourcentage écrit, lui, dit la vérité.
 * ⚠️ RIEN D'ACQUIS ⇒ PAS DE PART : « 0 % consommé » affirmerait un programme qui tourne sans être
 * utilisé, là où il ne s'est rien passé.
 *
 * ⚠️ LE VOCABULAIRE EST CELUI DE LA MAQUETTE (repris le 2026-09-05, « la version actuelle n'est pas
 * assez claire au niveau des termes »). L'écran disait « cagnotte acquise » et « cagnotte rendue » :
 * le même mot pour deux choses différentes, et « rendue » pris du point de vue du MAGASIN alors que
 * la légende juste en dessous parlait du point de vue du CLIENT. La maquette raconte UNE histoire —
 * acquis, dépensé, ce qui reste — et c'est celle-là qui se lit.
 *
 * ⚠️ EN EUROS, PAS EN POINTS, ET C'EST LE SEUL ÉCART ASSUMÉ AVEC LA MAQUETTE. Elle affiche « Points
 * acquis » ; notre programme ne compte PAS en points. Le grand livre est en centimes de bout en bout
 * (`credit_cents`, `debit_cents`, `loyalty_balance_cents`) : aucun taux de conversion n'existe dans le
 * schéma. Afficher un nombre de points supposerait un barème inventé (§23.0) — et il serait faux dès
 * le premier magasin qui n'utilise pas celui qu'on aurait deviné.
 *
 * ⚠️ LE RESTE EST CHIFFRÉ, il ne l'était pas. La légende disait « en attente chez les clients » sans
 * montant : la tuile posait donc une question sans y répondre, alors que la soustraction est immédiate.
 */
export function LoyaltyCard({ totals, period }: {
  totals: NonNullable<DashboardView['loyalty']>;
  period: string;
}) {
  const shareBp = totals.usageBp;
  const filled = shareBp === null ? 0 : Math.min(100, shareBp / 100);
  // ⚠️ PEUT ÊTRE NÉGATIF, et c'est un cas RÉEL : on dépense en juillet une cagnotte gagnée en juin.
  //    La maquette ne le rencontre jamais (ses chiffres sont fabriqués). Le dire est plus utile que
  //    d'afficher « -142,00 € restés en cagnotte », qui se lit comme une erreur de calcul.
  const remainingCents = totals.earnedCents - totals.returnedCents;
  return (
    <div className="dcard" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px',
        borderBottom: '1px solid var(--ink-200)',
      }}>
        <span style={{
          width: 28, height: 28, borderRadius: 'var(--r-xs)', flexShrink: 0,
          background: 'var(--teal-100)', color: 'var(--teal-700)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name="star" size={15} stroke={1.9} />
        </span>
        <span style={{ flex: 1, fontSize: 'var(--t-h4)', fontWeight: 700, letterSpacing: '-0.015em', color: 'var(--ink-900)' }}>
          
          {t('Fidélité')}
        </span>
        <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-400)', whiteSpace: 'nowrap' }}>{period}</span>
      </div>

      <div style={{ padding: '18px 20px 16px' }}>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 96 }}>
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)', letterSpacing: '.09em',
              textTransform: 'uppercase', color: 'var(--ink-500)', whiteSpace: 'nowrap',
            }}>{t('Acquis sur la période')}</div>
            <div style={{
              fontWeight: 700, color: 'var(--ink-900)',
              fontSize: DASHBOARD_FIGURES.loyaltyFigureSize,
              letterSpacing: DASHBOARD_FIGURES.loyaltyFigureTracking,
              fontVariantNumeric: 'tabular-nums', marginTop: 6,
            }}>{euroRond(totals.earnedCents)}</div>
            {/* ⚠️ LE CADEAU DU MAGASIN SE LIT (décision utilisateur) : le bonus de bienvenue et le crédit posé à la main
                sont DANS l'acquis, et la ligne dit leur part — jamais un montant de plus. */}
            {totals.offeredCents > 0 ? (
              <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 2, whiteSpace: 'nowrap' }}>
                {t('dont offert : {montant}', { montant: euroRond(totals.offeredCents) })}
              </div>
            ) : null}
          </div>
          <div style={{ width: 1, background: 'var(--ink-200)' }} />
          <div style={{ flex: 1, minWidth: 96 }}>
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)', letterSpacing: '.09em',
              textTransform: 'uppercase', color: 'var(--ink-500)', whiteSpace: 'nowrap',
            }}>{t('Dépensé par les clients')}</div>
            <div style={{
              fontWeight: 700, color: 'var(--ink-900)',
              fontSize: DASHBOARD_FIGURES.loyaltyFigureSize,
              letterSpacing: DASHBOARD_FIGURES.loyaltyFigureTracking,
              fontVariantNumeric: 'tabular-nums', marginTop: 6,
            }}>{euroRond(totals.returnedCents)}</div>
            {/* ⚠️ LA VENTILATION EST DUE, parce que les deux schémas ne se règlent pas pareil : le
                « cashback » est déduit à la caisse, le « bon d'achat » remet un titre que le client
                dépensera plus tard. Le total seul cacherait lequel des deux fait vivre le programme. */}
            {totals.vouchersCents > 0 ? (
              <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 2, whiteSpace: 'nowrap' }}>
                {t('dont {montant} en bons', { montant: euroRond(totals.vouchersCents) })}
              </div>
            ) : null}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 3, marginTop: 16 }}>
          <div style={{ flex: filled, height: 8, borderRadius: 'var(--r-pill)', background: 'var(--violet)' }} />
          <div style={{ flex: 100 - filled, height: 8, borderRadius: 'var(--r-pill)', background: 'var(--ink-200)' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 9, flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-dense)', color: 'var(--ink-600)' }}>
            <span aria-hidden style={{ width: 8, height: 8, borderRadius: 'var(--r-r3)', background: 'var(--violet)' }} />
            {shareBp === null ? t('rien d’acquis sur la période') : t('dépensé · {v}', { v: pourcent(shareBp, 0) })}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-dense)', color: 'var(--ink-600)' }}>
            <span aria-hidden style={{ width: 8, height: 8, borderRadius: 'var(--r-r3)', background: 'var(--ink-200)' }} />
            {remainingCents >= 0
              ? t('resté en cagnotte : {v}', { v: euroRond(remainingCents) })
              : t('dépensé au-delà de l’acquis de la période : {v}', { v: euroRond(-remainingCents) })}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * CE QUE LES DESTINATAIRES D'UNE CAMPAGNE ONT ACHETÉ — la phrase, ou `null` quand il n'y a rien à dire.
 *
 * ⚠️ 🔴 LE VOCABULAIRE EST LA MOITIÉ DU TRAVAIL. « 612,00 € attribués » se lit « la campagne a rapporté
 * 612 € » — et c'est faux : une partie de ces achats serait arrivée sans rien envoyer. La phrase dit
 * donc « chiffre d'affaires DES DESTINATAIRES dans les 14 jours », avec la fenêtre écrite en toutes
 * lettres. C'est plus long, et c'est la seule formulation qui ne fait pas relancer une campagne inutile.
 *
 * ⚠️ LA FENÊTRE VIENT DU SERVEUR, jamais d'une constante recopiée ici : deux endroits qui décident de
 * la même durée finissent par ne plus dire la même (forme A).
 *
 * ⚠️ AUCUN DESTINATAIRE SERVI ⇒ AUCUNE PHRASE. Une campagne qui n'est pas partie n'a rien à montrer, et
 * « 0,00 € » s'y lirait « elle n'a rien rapporté » au lieu de « elle n'a pas encore servi ».
 * ⚠️ ZÉRO ACHETEUR SE DIT, LUI : la campagne a bien servi, et personne n'est revenu — c'est une
 * réponse, et la taire laisserait croire à une mesure manquante.
 * ⚠️ PURE ET EXPORTÉE : c'est elle que la garde mesure, sans monter d'écran (comme `phraseParc`).
 */
export function phraseAttribution(a: CampaignAttribution | null): string | null {
  if (!a || a.served === 0) return null;
  if (a.buyers === 0) return t('aucun destinataire n’est revenu dans un délai de {jours}', { jours: t('{n} jour', { n: a.windowDays }) });
  const part = Math.round((a.buyers * 100) / a.served);
  // ⚠️ UNE SEULE PHRASE COMPTÉE, et le VERBE s'accorde DEDANS : passé en valeur (« ont »/« a »), il
  //    resterait français dans toutes les langues — « 3 recipients … ont bought » —, aucun catalogue ne
  //    pouvant traduire un mot qui n'est pas une clé (Forme DS).
  return t('{n} destinataire sur {served} ({part} %) a acheté dans un délai de {jours} — {amount} de leur chiffre d’affaires, dont la campagne n’est pas forcément la cause', {
    n: a.buyers, served: a.served, part, jours: t('{n} jour', { n: a.windowDays }), amount: euro(a.ttcCents),
  });
}


/**
 * CAMPAGNES — la tuile du tableau de bord, relevée sur la maquette.
 *
 * ⚠️ TROIS LIGNES, PAS UN COMPTEUR. Comme les écarts d'à côté, elle NOMME les campagnes plutôt que
 * d'en donner le nombre : « 3 campagnes » ne permet de décider de rien, alors que « Soldes acte 2,
 * prête, 1 240 destinataires » se lit et se tranche depuis le tableau de bord.
 *
 * ⚠️ CE QUE LA MAQUETTE MONTRE ET QUE NOUS NE MESURONS PAS. Elle affiche « 51 ouverts · 7 coupons
 * utilisés · 612,00 € attribués », puis « Lift +3,1 pts vs témoin · ROI 4,8× ». Rien de tout cela
 * n'existe : il n'y a ni pixel d'ouverture, ni rattachement d'un coupon à une vente, ni groupe
 * témoin. Les recopier donnerait des chiffres plausibles et faux sur l'écran où l'on décide de
 * relancer une campagne (§23.0). La tuile dit donc ce que l'envoi a RÉELLEMENT produit — partis,
 * échoués, écartés — et la mesure de l'effet reste au bloc « pas encore mesuré ».
 *
 * ⚠️ L'ORDRE N'EST PAS CELUI DE LA LISTE. Le module marketing trie par date de création ; ici on veut
 * ce qui APPELLE UNE DÉCISION : ce qui tourne, puis ce qui attend un départ, puis ce qui vient de
 * finir. Une campagne en cours reléguée en troisième position par une date de création ancienne
 * serait invisible au moment précis où elle compte.
 */
function Campagnes() {
  const nav = useNavigate();
  const [rows, setRows] = useState<Campaign[] | null>(null);
  /** Ce que les destinataires ont acheté, par campagne — servi dans le MÊME appel que les lignes. */
  const [attrib, setAttrib] = useState<ReadonlyMap<string, CampaignAttribution>>(new Map());
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    api.campaigns()
      .then((v) => {
        if (!vivant) return;
        setRows(v.rows);
        setAttrib(new Map(v.attribution.map((a) => [a.campaignId, a])));
        setFailure(null);
      })
      .catch((e: unknown) => { if (vivant) { setRows(null); setFailure(e instanceof Error ? e.message : String(e)); } });
    return () => { vivant = false; };
  }, []);

  const top = useMemo(() => (rows ?? []).slice().sort((a, b) => {
    const r = RANG_CAMPAGNE(a) - RANG_CAMPAGNE(b);
    return r !== 0 ? r : (b.finishedAt ?? b.startedAt ?? b.createdAt) - (a.finishedAt ?? a.startedAt ?? a.createdAt);
  }).slice(0, 3), [rows]);

  return (
    <div style={{
      background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)',
      overflow: 'hidden', height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px',
        borderBottom: '1px solid var(--ink-200)',
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: 'var(--r-xs)', background: 'var(--violet-bg)',
          color: 'var(--violet-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon name="mail" size={15} stroke={1.9} />
        </div>
        <span style={{ fontSize: 'var(--t-h4)', fontWeight: 700, letterSpacing: '-0.015em', color: 'var(--ink-900)', flex: 1 }}>
          
          {t('Campagnes')}
        </span>
        {/* ⚠️ « Tout voir » EST UN VRAI LIEN, pas la décoration de la maquette : un renvoi qui ne
            renvoie nulle part est un bouton qui fait semblant (§23.0). */}
        <button type="button" onClick={() => nav('/marketing')} style={{
          border: 0, background: 'transparent', padding: 0, cursor: 'pointer',
          fontSize: 'var(--t-body)', fontWeight: 500, color: 'var(--teal-700)',
        }}>{t('Tout voir')}</button>
      </div>

      {failure !== null ? (
        <p role="alert" style={{ margin: 0, padding: '16px 20px', fontSize: 'var(--t-note)', color: 'var(--danger-ink)', lineHeight: 1.45 }}>
          {t('Les campagnes n’ont pas pu être lues : {failure}', { failure })}
        </p>
      ) : rows === null ? (
        <p style={{ margin: 0, padding: '16px 20px', fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{t('Lecture…')}</p>
      ) : top.length === 0 ? (
        /* ⚠️ L'ÉTAT VIDE DE LA CHARTE, pas une ligne de texte (demande utilisateur 2026-09-05). C'est
           le MÊME composant que l'écran Marketing emploie pour le même vide : deux dessins pour la
           même absence finiraient par ne plus dire la même chose.
           ⚠️ CRAN `compact` : la tuile occupe une demi-rangée à côté des écarts, et le gabarit plein
           la ferait dépasser sa voisine — une carte qui grandit quand elle n'a rien à montrer.
           ⚠️ ET CE VIDE-LÀ APPELLE UNE ACTION, contrairement à celui des écarts d'à côté : « aucun
           écart » est une BONNE nouvelle qui ne demande rien, « aucune campagne » est un outil qu'on
           n'a pas encore employé. Les deux tuiles ne se traitent donc pas pareil, et c'est voulu. */
        <EmptyState
          compact
          icon="mail"
          title={t('Aucune campagne')}
          note={t('Les campagnes s’écrivent, se testent et s’envoient depuis le module Marketing.')}
          action={<Button variant="ghost" onClick={() => nav('/marketing')}>{t('Ouvrir le marketing')}</Button>}
        />
      ) : (
        top.map((c, i) => (
          <LigneDeCampagne key={c.id} c={c} a={attrib.get(c.id) ?? null} dernier={i === top.length - 1} />
        ))
      )}
    </div>
  );
}

/**
 * L'ORDRE D'ATTENTION : ce qui tourne, ce qui a échoué, ce qui attend, ce qui a fini.
 * ⚠️ L'ÉCHEC PASSE AVANT L'ATTENTE : une campagne interrompue est la seule qui perd des destinataires
 * pendant qu'on ne la regarde pas.
 */
const RANG_CAMPAGNE = (c: Campaign): number =>
  ({ sending: 0, failed: 1, paused: 2, ready: 3, done: 4, draft: 5 } as Record<string, number>)[c.status] ?? 6;

/** Le ton et le mot de chaque état — les mêmes que l'écran Marketing, jamais un second vocabulaire. */
const ETAT_CAMPAGNE: Record<string, { label: Translatable; tone: BadgeTone }> = {
  draft: { label: key('Brouillon'), tone: 'neutral' },
  ready: { label: key('Prête'), tone: 'info' },
  sending: { label: key('Envoi en cours'), tone: 'info' },
  paused: { label: key('Suspendue'), tone: 'warn' },
  done: { label: key('Terminée'), tone: 'ok' },
  failed: { label: key('En échec'), tone: 'danger' },
};

/**
 * Une campagne : son état, son nom, et ce que l'envoi a produit.
 * ⚠️ LA SECONDE LIGNE DIT CE QUI EST VRAI POUR CET ÉTAT-LÀ. Une phrase unique (« N envoyés sur M »)
 * serait fausse pour un brouillon, dont l'audience n'est même pas figée.
 */
function LigneDeCampagne({ c, a, dernier }: { c: Campaign; a: CampaignAttribution | null; dernier: boolean }) {
  // L'état connu porte une CLÉ, un état inconnu se montre tel quel — la clé se résout ici, une seule fois.
  const connu = ETAT_CAMPAGNE[c.status];
  const etat = connu ? { label: t(connu.label), tone: connu.tone } : { label: c.status, tone: 'neutral' as BadgeTone };
  // ⚠️ LA TABLE PARTAGÉE, PAS UN TERNAIRE : un canal ajouté au type s'affichait « E-mail » ici sans
  //    qu'aucune porte ne le voie (Forme EA). `channelLabel` oblige le compilateur à le nommer.
  const canal = channelLabel(c.channel);
  // ⚠️ LES ÉCHECS ET LES ÉCARTÉS NE SE TAISENT PAS. Repliés dans le détail, ils obligeraient à ouvrir
  //    chaque campagne pour savoir laquelle a mal fini — or c'est la question que pose cette tuile.
  const reste = [
    c.failedCount ? t('{v} en échec', { v: nb(c.failedCount) }) : null,
    c.skippedCount ? t('{v} écartés', { v: nb(c.skippedCount) }) : null,
  ].filter(Boolean).join(' · ');

  const dit = c.status === 'sending'
    ? t('{canal} · {v} traités sur {v2}', { canal, v: nb(c.processed), v2: nb(c.recipientCount) })
    : c.status === 'done'
      // ⚠️ DEUX PHRASES ENTIÈRES : « le {date} » était un trou de plus, donc un fragment livré au
      //    traducteur sans sa phrase — et l'ordre des mots change d'une langue à l'autre.
      ? c.finishedAt
        ? t('{canal} · {v} envoyés le {date}', { canal, v: nb(c.sentCount), date: dateCompact(c.finishedAt) })
        : t('{canal} · {v} envoyés', { canal, v: nb(c.sentCount) })
      : c.status === 'ready'
        ? t('{canal} · {v} destinataires figés, en attente de départ', { canal, v: nb(c.recipientCount) })
        : c.status === 'draft'
          ? t('{canal} · audience non figée', { canal })
          : c.status === 'paused'
            ? t('{canal} · interrompue au {v}ᵉ sur {v2}', { canal, v: nb(c.processed), v2: nb(c.recipientCount) })
            // ⚠️ LE MOTIF RÉEL, PAS UN « une erreur est survenue » : c'est lui qui dit s'il faut
            //    relancer ou corriger le message (§23.0).
            : `${canal} · ${c.lastError ?? t('interrompue, motif non enregistré')}`;

  return (
    <div style={{
      padding: '13px 20px',
      borderBottom: dernier ? 'none' : '1px solid var(--ink-100)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
        <Badge size="xs" tone={etat.tone}>{etat.label}</Badge>
        <span style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-900)' }}>{c.name}</span>
      </div>
      <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>
        {dit}{reste ? ` · ${reste}` : ''}
        {/* ⚠️ SUR SA PROPRE LIGNE, et en retrait : ce n'est pas du même ordre que ce qui précède. Au
            dessus, ce que l'ENVOI a produit — mesuré, certain. Ici, ce que les destinataires ont
            acheté ensuite — vrai, mais pas causé par la campagne. Les mettre bout à bout dans la même
            phrase les ferait lire comme une seule mesure. */}
        {phraseAttribution(a) && (
          <div style={{ marginTop: 3, color: 'var(--ink-500)', fontSize: 'var(--t-meta)' }}>
            {phraseAttribution(a)}
          </div>
        )}
        {/* ⚠️ ENCORE UNE LIGNE À PART, et pour la même raison qu'au-dessus : celle-ci parle de CAUSE.
            Au-dessus, ce que les destinataires ont acheté — vrai, mais pas causé par la campagne.
            Ici, ce que la campagne a changé, mesuré contre des gens qui n'ont rien reçu. Les coller
            dans la même phrase ferait lire le premier chiffre comme le second. */}
        {phraseEcart(a) && (
          <div style={{
            marginTop: 3, fontSize: 'var(--t-meta)',
            color: a?.lift?.conclusive ? 'var(--ink-700)' : 'var(--ink-500)',
          }}>
            {phraseEcart(a)}
          </div>
        )}
      </div>

      {/* ⚠️ LA BARRE N'APPARAÎT QUE PENDANT L'ENVOI, et seulement si l'audience est connue : une jauge
          de progression sur une campagne terminée n'informe de rien, et sur un brouillon elle
          diviserait par zéro. */}
      {c.status === 'sending' && c.recipientCount > 0 ? (
        <div style={{ display: 'flex', gap: 3, marginTop: 9, height: 5 }}>
          <div style={{
            flex: Math.min(100, (c.processed / c.recipientCount) * 100),
            borderRadius: 'var(--r-pill)', background: 'var(--teal-500)',
          }} />
          <div style={{
            flex: Math.max(0, 100 - (c.processed / c.recipientCount) * 100),
            borderRadius: 'var(--r-pill)', background: 'var(--ink-200)',
          }} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * CE QUE CET ÉCRAN NE MESURE PAS ENCORE — nommé, et dit pourquoi.
 *
 * ⚠️ La maquette prévoit ces cartes. Les dessiner remplies de valeurs plausibles serait exactement
 * l'interdit du §23.0 ; les dessiner vides sans explication ferait croire à une panne. On les nomme.
 * ⚠️ ET ON LES RETIRE D'ICI DÈS QU'ELLES EXISTENT : un bloc qui annonce comme manquante une carte
 * affichée juste au-dessus discrédite tout ce qu'il énumère — or il n'a que sa parole.
 */
/**
 * CE QUI MANQUE POUR MESURER L'EFFET D'UNE CAMPAGNE — relevé contre la maquette, pas de mémoire.
 *
 * ⚠️ LA MAQUETTE EN MONTRE QUATRE, ET ILS N'ONT PAS LE MÊME COÛT : « 51 ouverts · 7 coupons utilisés ·
 * 612,00 € attribués », puis « Lift +3,1 pts vs témoin · ROI 4,8× (coût 62,00 €) ». Les recopier
 * donnerait des chiffres plausibles et faux sur l'écran où l'on décide de relancer une campagne.
 *
 * ⚠️ UNE NOTE D'EXCLUSION INCOMPLÈTE EST PIRE QUE PAS DE NOTE — la leçon est écrite ailleurs dans ce
 * dépôt, sur une liste de champs qui déclarait absents neuf champs bien présents. Celle-ci en omettait
 * DEUX, et chacun envoie chercher au mauvais endroit :
 *   · le **ROI** demande un COÛT D'ENVOI, et aucun prix unitaire n'existe nulle part dans le module —
 *     l'attribution branchée, il resterait incalculable. Ce n'est pas un corollaire des trois autres.
 *   · les **ouvertures n'existent pas en SMS**, et ça ne se construira jamais : c'est une limite du
 *     canal, pas un chantier. Les annoncer « à venir » sans le dire promettrait l'impossible.
 *
 * ⚠️ CE QUI EST DÉJÀ MESURÉ EST DIT AUSSI : l'envoi (partis, échoués, écartés) l'est, ligne par ligne.
 * Un bloc qui ne dirait que les manques laisserait croire que la tuile d'à côté n'affiche rien de sûr.
 */
export const MANQUE_EFFET_CAMPAGNE =
  // ⚠️ UNE SEULE CLÉ : la phrase était écrite en quatre littéraux collés — une commodité de mise en
  //    page du fichier, que le déclarateur a prise pour quatre textes. Un traducteur n'aurait reçu
  //    que des bouts, sans leur ordre.
  key('ni pixel d’ouverture (et le SMS n’en aura jamais : le canal ne le permet pas), ni rattachement d’un coupon à une vente, ni coût d’envoi pour un ROI. Ce qui EST mesuré : l’envoi ligne par ligne (partis, échoués, écartés), ce que les destinataires ont acheté ensuite, et — quand la campagne a réservé un groupe témoin — l’écart que la campagne a réellement causé.');

/**
 * Le bloc « Ce que ce tableau ne mesure pas encore ».
 * ⚠️ EXPORTÉ pour que son banc monte la ligne « Marge » seule, sans les lectures de tout le tableau de bord.
 */
export function NotYetMeasured({ gap, query }: { gap: SalesMarginGap | null; query: MissingCostsParams }) {
  // ⚠️ LISTE À TENIR À JOUR AVEC L'ÉCRAN. Chaque carte construite en sort le jour même : y laisser une
  // carte désormais présente ferait douter de tout le reste du bloc — et c'est le bloc dont le seul
  // rôle est d'être cru sur parole. Météo, taux de transformation et fidélité en sont sortis (2026-08-19).
  // ⚠️ LA RAISON DOIT ÊTRE VRAIE, elle aussi. « Écarts à traiter » a longtemps été annoncé ici comme
  // « écarts de caisse, clôtures non remontées » — deux erreurs : la maquette y met des anomalies de
  // STOCK (transfert incomplet, vente sur stock négatif, inversion de taille), et les clôtures, elles,
  // remontent bien. Une raison fausse dans le bloc qu'on demande de croire sur parole vaut une carte
  // fausse (§23.0). Corrigé le 2026-08-19, après mesure des bases.
  // ⚠️ « ÉCARTS À TRAITER » EST SORTI D'ICI (2026-08-29), en appliquant la règle ci-dessus. La carte
  //    existe, elle lit de vrais écarts, et sa réserve — « le stock de ce magasin est intégralement à
  //    zéro » — est désormais portée PAR LA CARTE, mesurée par magasin (`stockExploitable`). La
  //    laisser ici l'annonçait comme non mesurée pour TOUS les magasins, y compris ceux dont le stock
  //    est fiable : une réserve juste, appliquée là où elle est fausse, est un mensonge de plus.
  // ⚠️ « CAMPAGNES » EST SORTI D'ICI (2026-09-05) : la tuile existe et lit de vraies campagnes. Ce qui
  //    reste non mesuré n'est plus le module, c'est l'EFFET d'une campagne — la maquette montre
  //    ouvertures, coupons utilisés, chiffre d'affaires attribué et comparaison à un groupe témoin,
  //    et aucun des quatre n'a de source. C'est cette phrase-là qui est vraie aujourd'hui.
  // ⚠️ LA CLÉ SE RÉSOUT ICI : rendue nue plus bas (`{pourquoi}`), elle s'affichait en français pour tous les comptes.
  const manques: [string, ReactNode][] = [[t('Effet des campagnes'), t(MANQUE_EFFET_CAMPAGNE)]];
  // ⚠️ LA MARGE DIT SES ARTICLES PAR L'ENCART COMMUN : la même cause, le même compte et la même liste que le Résumé.
  if (gap && gap.lines > 0) manques.unshift([t('Marge'), <MissingCostNotice gap={gap} query={query} compact />]);
  return (
    <div className="dcard" style={{ padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8 }}>
        <Icon name="info" size={15} />
        <span style={{ fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Ce que ce tableau ne mesure pas encore')}</span>
      </div>
      <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--ink-500)', fontSize: 'var(--t-body)', lineHeight: 1.7 }}>
        {manques.map(([what, pourquoi]) => (
          <li key={what}><strong style={{ color: 'var(--ink-700)', fontWeight: 600 }}>{what}</strong> — {pourquoi}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Ce qu'une barre couvre, dit en toutes lettres sous le total.
 * ⚠️ LE DÉTECTEUR NE LES VOYAIT PAS : « par heure » n’a ni majuscule, ni accent, ni ponctuation —
 *    rien de ce à quoi il reconnaît un texte. Déclarés à la main, ils entrent au catalogue ; sans
 *    ça, quatre libellés d'écran seraient restés français sans que rien ne le signale.
 */
const LIBELLE_GRAIN: Record<string, Translatable> = {
  hour: key('par heure'), day: key('par jour'), week: key('par semaine'), month: key('par mois'),
};

/** Le grain, RÉSOLU au rendu — un grain inconnu ne dit rien plutôt que d'afficher son code. */
const grainLisible = (grain: string): string => (LIBELLE_GRAIN[grain] ? t(LIBELLE_GRAIN[grain]!) : '');

/**
 * Le nom court d'un mois — D'`Intl`, JAMAIS d'une table écrite à la main.
 * 🔴 DEUXIÈME OCCURRENCE DU MÊME DÉFAUT (la première vivait dans la fiche client) : une table de
 *    douze mois en français pur, dont la migration ne reconnaît comme textes que ceux qui portent
 *    un accent ou un point — trois sur douze partaient au catalogue, neuf restaient en dur.
 * ⚠️ LU AU RENDU (`currentLang()`) : la langue du compte n'est pas encore là au chargement.
 */
const moisCourt = (mois: number): string =>
  new Intl.DateTimeFormat(currentLang(), { month: 'short' }).format(new Date(Date.UTC(2000, mois - 1, 1)));

/**
 * L'étiquette d'axe d'un seau, selon son grain.
 * ⚠️ La semaine s'annonce « s. 17/08 » et non « 17/08 » : sans le préfixe, une barre hebdomadaire se
 * lit comme une journée, et le lecteur divise mentalement le chiffre par sept.
 */
function etiquetteSeau(bucket: string, grain: string): string {
  if (grain === 'hour') return `${bucket} h`;
  if (grain === 'month') {
    const mois = Number(bucket.slice(4, 6));
    return mois >= 1 && mois <= 12 ? moisCourt(mois) : bucket;
  }
  const jm = `${bucket.slice(6, 8)}/${bucket.slice(4, 6)}`;
  return grain === 'week' ? `s. ${jm}` : jm;
}

/**
 * Le dessin d'un temps. ⚠️ Choisi sur la FAMILLE et non sur le libellé, qui est traduit et varie.
 * ⚠️ UNE ICÔNE PAR FAMILLE RÉELLEMENT RENVOYÉE par le service — pas une de plus : une famille sans
 * source serait une icône que personne ne verrait jamais.
 */
const ICONE_TEMPS: Record<string, IconName> = {
  clear: 'sun', fewClouds: 'sunCloud', clouds: 'cloud',
  drizzle: 'drizzle', rain: 'rain', freezingRain: 'freezingRain',
  sleet: 'sleet', snow: 'snow',
  storm: 'storm', fog: 'fog', haze: 'haze', wind: 'wind', tornado: 'tornado',
};

/** Ce qui manque, dit là où ça se corrige — jamais un « indisponible » qui n'indique rien. */
const MANQUE_METEO: Record<string, Translatable> = {
  not_configured: key('Service météo non configuré sur ce serveur.'),
  no_location: key('Ce magasin n’a pas de code postal renseigné — la météo est celle du magasin, pas la vôtre.'),
  unsupported_country: key('Pays du magasin hors de la couverture actuelle.'),
  provider_failed: key('Le service météo n’a pas répondu.'),
};

/**
 * LA MÉTÉO DU MAGASIN — un commerce vend autrement sous la pluie, et c'est une aide à la lecture des
 * chiffres du jour autant qu'à la décision de sortir un présentoir.
 *
 * ⚠️ ELLE SE CHARGE À PART DU TABLEAU DE BORD : la route appelle un service extérieur, et l'attendre
 * dans la même requête ferait patienter le chiffre d'affaires derrière un tiers.
 * ⚠️ ELLE EST SITUÉE PAR LE CODE POSTAL DU MAGASIN, pas par l'adresse IP : le back-office se consulte
 * à distance, et l'IP donnerait la météo de qui regarde — Paris pour un magasin niçois.
 * ⚠️ ELLE NE DEVINE RIEN : sans donnée, elle dit ce qui manque et OÙ ça se corrige (§23.0).
 */
function useMeteo(): { vue: WeatherView | null; failure: string | null } {
  const [vue, setVue] = useState<WeatherView | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  useEffect(() => {
    let vivant = true;
    api.weather()
      .then((v) => { if (vivant) setVue(v); })
      .catch((e: unknown) => { if (vivant) setFailure(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, []);
  return { vue, failure };
}

function Meteo({ vue, failure }: { vue: WeatherView | null; failure: string | null }) {
  // ⚠️ ELLE GARDE SA CELLULE PENDANT LA LECTURE. Rendre `null` en attendant la réponse ferait
  // reculer les quatre tuiles voisines d'une colonne, puis sauter en arrière à l'arrivée du service —
  // un déplacement que rien à l'écran n'explique.
  const waiting = !vue && !failure;

  // ⚠️ LA PASTILLE PREND LA COULEUR DU CIEL (demande utilisateur 2026-09-05). Elle n'avait que deux
  //    états — ambre par ciel dégagé, encre pour tout le reste — si bien que neige, orage et
  //    brouillard se peignaient à l'identique. Le ton vit dans la charte (`WEATHER_TONES`) et le
  //    dessin dans `WeatherPill` : cet écran ne décide plus d'aucune couleur.
  const temps = vue?.ok ? vue.kind : null;
  const kind = vue?.ok ? (ICONE_TEMPS[vue.kind] ?? 'cloud') : 'cloud';
  return (
    <div className="dcard" style={{ padding: 18, height: '100%', display: 'flex', alignItems: 'center', gap: 14, boxSizing: 'border-box' }}>
      <WeatherPill kind={temps} icon={kind} />

      <div style={{ minWidth: 0, flex: 1 }}>
        {waiting ? (
          <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-400)' }}>{t('Météo en cours de lecture…')}</div>
        ) : vue?.ok ? (
          <>
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)', letterSpacing: '.09em',
              textTransform: 'uppercase', color: 'var(--ink-500)', whiteSpace: 'nowrap',
            }}>{vue.city}</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginTop: 5 }}>
              <span style={{
                fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.03em', color: 'var(--ink-900)',
                fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
              }}>{vue.tempC}°</span>
              {/* ⚠️ TOUJOURS AFFICHÉ, même quand il égale la température. Il ne l'était que s'il en
                  différait — mais les deux valeurs coïncident souvent une fois arrondies (28,75 et
                  28,53 donnent 29 toutes les deux), et la ligne disparaissait alors comme si le
                  service ne la fournissait pas. Une tuile qui change de forme selon le temps qu'il
                  fait se lit comme une donnée manquante. */}
              <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>
                {t('ressenti {v}°', { v: vue.feelsC })}
              </span>
            </div>
            <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-600)', textTransform: 'capitalize' }}>
              {vue.description}
            </div>
          </>
        ) : (
          <>
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)', letterSpacing: '.09em',
              textTransform: 'uppercase', color: 'var(--ink-500)',
            }}>{t('Météo')}</div>
            <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-600)', marginTop: 5, lineHeight: 1.45 }}>
              {failure ?? t(MANQUE_METEO[(vue as { reason: string }).reason] ?? key('Indisponible.'))}
              {vue && !vue.ok && vue.detail ? <span style={{ color: 'var(--ink-400)' }}> ({vue.detail})</span> : null}
            </div>
          </>
        )}
      </div>

    </div>
  );
}

/**
 * LES CLIENTS IDENTIFIÉS — une JAUGE, pas une tuile à courbe.
 *
 * ⚠️ C'EST LE DESSIN DE LA MAQUETTE, et il dit autre chose qu'un pourcentage posé sur une ligne : un
 * taux d'identification se lit contre son maximum (« quelle part de mes ventes sait à qui elle est
 * faite ? »), pas comme une tendance. La tuile plate qui l'affichait détonnait entre ses voisines à
 * mini-courbe, et laissait croire à un indicateur en panne.
 * ⚠️ LE DÉCOMPTE VIENT DU SERVEUR, jamais d'un produit taux × ventes : l'arrondi du taux ferait
 * afficher « 411 tickets sur 1 084 » sous un anneau qui en annonce 38 %, et les deux chiffres ne
 * s'accorderaient pas.
 */
function ClientsIdentifies({ bp, identifiees, total }: {
  bp: number; identifiees: number; total: number;
  /** Ouvre le rapport des fiches qui faussent ce taux — à un clic du chiffre qu'elles faussent. */
}) {
  const n = (v: number) => v.toLocaleString('fr-FR');
  return (
    <div className="dcard" style={{
      padding: 18, height: '100%', boxSizing: 'border-box',
      display: 'flex', alignItems: 'center', gap: 16,
    }}>
      {/* ⚠️ LES PALIERS VIENNENT DU CŒUR, pas de cet écran (arbitrage utilisateur 2026-09-04) : sous
          15 % le fichier client ne représente plus l'activité, au-delà de 30 % il est exploitable.
          Écrits ici, ils seraient recopiés au deuxième écran qui montre le même taux. */}
      <Gauge valueBp={bp} size={66} bands={IDENTIFIED_SALES_BANDS}
        label={t('{v} des ventes rattachées à une fiche client', { v: pourcent(bp, 0) })}>
        <span style={{
          fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums',
        }}>{pourcent(bp, 0)}</span>
      </Gauge>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)', letterSpacing: '.09em',
          textTransform: 'uppercase', color: 'var(--ink-500)',
        }}>{t('Clients identifiés')}</div>
        <div style={{ fontSize: 'var(--t-lead)', color: 'var(--ink-600)', marginTop: 5, lineHeight: 1.45 }}>
          {/* ⚠️ UNE SEULE CLÉ : le `<br />` est de la mise en forme, pas une frontière de sens. En
              deux clés, le traducteur recevait « N tickets sur M » et « rattachés à une fiche »
              séparément — sans l'accord, sans l'ordre, et sans savoir que c'est une phrase. */}
          <Trans k='<1>{n} tickets sur {total}</1> rattachés à une fiche'
            vars={{ n: identifiees, total: n(total) }}
            parts={[(x: ReactNode) => <><span style={{ fontVariantNumeric: 'tabular-nums' }}>{x}</span><br /></>]} />
        </div>
              </div>
    </div>
  );
}

/**
 * ÉCARTS À TRAITER — la dernière tuile de la maquette, et la seule qui refusait d'être bâtie.
 *
 * ⚠️ POURQUOI ELLE A ATTENDU. La maquette y liste trois anomalies de stock ; deux d'entre elles
 * n'ont AUCUN mécanisme derrière (« transfert reçu incomplet » suppose un module de transferts
 * inter-magasins qui n'existe pas ; « inversion de taille probable » est une heuristique jamais
 * cadrée). Et la troisième — le stock négatif — se lisait sur un stock repris qui portait 2 040
 * lignes négatives : la tuile aurait annoncé NOTRE trou d'import comme une anomalie du commerçant.
 * La reprise refaite ramène ce chiffre à 12 : la détection redevient une information.
 *
 * ⚠️ ELLE NE COMPTE QUE CE QU'ELLE SAIT VRAIMENT DÉTECTER, et le dit. Afficher trois catégories dont
 * deux resteront éternellement à zéro ferait croire à une surveillance qui n'a pas lieu (§23.0).
 *
 * ⚠️ ET « AUCUN ÉCART » N'EST PAS « JE NE SAIS PAS ». Un magasin dont le stock n'a jamais été
 * renseigné — mesuré : 59 069 lignes toutes à zéro — ne produit aucun écart parce qu'il n'y a rien à
 * lire, pas parce que tout va bien. Une pastille verte y serait le mensonge le plus confortable.
 */
/**
 * Une ligne de la tuile : ce qui ne colle pas, et de combien.
 *
 * ⚠️ FAIT, PAS DOCTRINE. Le `sub` du relevé porte l'explication métier (« la vente n'est jamais
 * bloquée… ») : elle a sa place sur la page, où on agit, pas dans une tuile qu'on balaie. Ici on
 * compose la ligne de la maquette — la valeur au coût, et depuis quand ça attend.
 */
function LigneDEcart({ e, dernier }: { e: EcartRow; dernier: boolean }) {
  const faits = [
    // ⚠️ `null` = coût inconnu, et ça se DIT : afficher « 0,00 € » ferait passer un manque non
    //    valorisé pour un manque sans valeur (§23.0).
    // ⚠️ PAS DE `€` ÉCRIT ICI : `euro()` pose déjà le symbole, et la ligne rendait « 178,00 € € au
    //    coût ». Vu à la capture du 2026-09-05, invisible à la relecture — les deux moitiés sont
    //    justes séparément, c'est leur juxtaposition qui doublait le signe.
    e.valueCents == null ? t('valeur inconnue') : t('{v} au coût', { v: euro(Math.abs(e.valueCents)) }),
    timeAgo(e.date),
  ].join(' · ');

  return (
    <div style={{
      padding: '12px 20px',
      borderBottom: dernier ? 'none' : '1px solid var(--ink-100)',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-900)' }}>{e.label}</span>
        {/* La puce mono ne paraît QUE si elle ajoute une référence que le libellé ne porte pas déjà :
            un n° de BL ou de caisse y est déjà écrit, le gencode non. */}
        {e.gencode && (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-400)' }}>{e.gencode}</span>
        )}
      </div>
      <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-600)', marginTop: 2 }}>{faits}</div>
    </div>
  );
}

/**
 * ÉCARTS À TRAITER — la tuile du tableau de bord, relevée sur la maquette.
 *
 * ⚠️ ELLE NOMME LES ÉCARTS, ELLE NE LES COMPTE PAS. La version précédente rendait un grand nombre et
 * une répartition par source (« 3 stock négatif · 2 caisse ») : on savait qu'il y avait du travail,
 * jamais lequel — il fallait ouvrir la page pour apprendre quoi que ce soit. La maquette montre les
 * trois premiers, et c'est ce qui permet de décider depuis le tableau de bord.
 *
 * ⚠️ CES TROIS LIGNES NE COÛTENT RIEN : le résumé lisait déjà le relevé complet côté serveur et
 * jetait les lignes pour n'en garder que des compteurs. Même requête, même ordre que la page.
 */
function EcartsATraiter() {
  const nav = useNavigate();
  const [r, setR] = useState<{ open: number; bySource: Record<string, number>; stockExploitable: boolean; top: EcartRow[] } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    api.variancesResume()
      .then((v) => { if (vivant) { setR(v); setFailure(null); } })
      .catch((e: unknown) => { if (vivant) { setR(null); setFailure(e instanceof Error ? e.message : String(e)); } });
    return () => { vivant = false; };
  }, []);

  return (
    <div style={{
      background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)',
      overflow: 'hidden', height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
    }}>
      {/* ── L'en-tête : pictogramme ambré, titre, et la pastille du COMPTE ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px',
        borderBottom: '1px solid var(--ink-200)',
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: 'var(--r-xs)', background: 'var(--amber-bg)',
          color: 'var(--amber-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M7 4L3 8l4 4" /><path d="M3 8h13a4 4 0 0 1 0 8h-1" />
            <path d="M17 20l4-4-4-4" /><path d="M21 16H8a4 4 0 0 1 0-8h1" />
          </svg>
        </div>
        <span style={{ fontSize: 'var(--t-h4)', fontWeight: 700, letterSpacing: '-0.015em', color: 'var(--ink-900)', flex: 1 }}>
          
          {t('Écarts à traiter')}
        </span>
        {r !== null && r.open > 0 && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px',
            borderRadius: 'var(--r-pill)', background: 'var(--amber-bg)', color: 'var(--amber-fg)',
            fontSize: 'var(--t-note)', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
          }}>{r.open}</span>
        )}
      </div>

      {failure !== null ? (
        <p role="alert" style={{ margin: 0, padding: '16px 20px', fontSize: 'var(--t-note)', color: 'var(--danger-ink)', lineHeight: 1.45 }}>
          {t('Le relevé n’a pas pu être lu : {failure}', { failure })}
        </p>
      ) : r === null ? (
        <p style={{ margin: 0, padding: '16px 20px', fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{t('Lecture…')}</p>
      ) : (
        <>
          {r.top.length === 0 ? (
            /* ⚠️ L'ÉTAT VIDE DE LA CHARTE, comme la tuile Campagnes d'à côté — sinon l'une paraît
               finie et l'autre pas, dans la même rangée.
               ⚠️ MAIS L'ICÔNE DÉPEND DU STOCK, et c'est la règle que cette tuile porte depuis sa
               naissance : « aucun écart » n'est pas « je ne sais pas ». Sur un magasin dont le stock
               est intégralement à zéro, rien n'a pu être comparé — une coche verte y serait le
               mensonge le plus confortable (§23.0). On rend alors un ton NEUTRE, et la réserve juste
               en dessous dit pourquoi.
               ⚠️ PAS D'ACTION ICI, contrairement aux campagnes : « aucun écart » est une bonne
               nouvelle qui n'appelle aucun geste, et le pied de tuile offre déjà le centre d'écarts. */
            <EmptyState
              compact
              icon={r.stockExploitable ? 'check' : 'info'}
              title={r.stockExploitable ? t('Aucun écart en attente') : t('Rien à comparer')}
              note={r.stockExploitable
                ? t('Les mouvements de stock et les encaissements ne montrent aucune anomalie.')
                : t('La détection a tourné, mais elle n’avait rien à lire.')}
            />
          ) : (
            r.top.map((e, i) => <LigneDEcart key={e.id} e={e} dernier={i === r.top.length - 1} />)
          )}

          {/* ⚠️ LA RÉSERVE PASSE AVANT LE ZÉRO RASSURANT : sur un magasin au stock jamais renseigné,
              « aucun écart » se lit « tout va bien » alors qu'on n'a rien pu regarder. */}
          {!r.stockExploitable && (
            <p style={{
              margin: 0, padding: '12px 20px', borderTop: '1px solid var(--ink-100)',
              fontSize: 'var(--t-body)', color: 'var(--amber-fg)', lineHeight: 1.45,
            }}>
              
              {t('Le stock de ce magasin est intégralement à zéro : sa reprise n’a trouvé aucune quantité. Les écarts de stock ne peuvent pas être détectés ici.')}
            </p>
          )}

          {/* Le pied colle en bas quelle que soit la hauteur de la tuile voisine. */}
          <div style={{ flex: 1 }} />
          <div style={{ padding: '12px 20px', background: 'var(--ink-50)', borderTop: '1px solid var(--ink-200)' }}>
            <button
              type="button"
              onClick={() => nav('/ecarts')}
              style={{
                width: '100%', height: 36, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
                background: 'var(--paper)', color: 'var(--ink-800)', fontSize: 'var(--t-lead)', fontWeight: 600, cursor: 'pointer',
              }}
            >
              {r.open > r.top.length
                // Le compte dit ce que le bouton ouvre : trois lignes affichées sur douze, la page en
                // montre neuf de plus — le taire ferait croire que tout tient dans la tuile.
                ? t('Ouvrir le centre d’écarts ({open})', { open: r.open })
                : t('Ouvrir le centre d’écarts')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * LE TAUX DE TRANSFORMATION — la carte est là, la MOITIÉ du calcul manque.
 *
 * ⚠️ UN TAUX DE TRANSFORMATION SE CALCULE SUR DES ENTRÉES, PAS SUR DES VENTES. Le nombre de tickets
 * est mesuré ; le nombre de PASSAGES ne l'est pas — il demande un compteur à l'entrée du magasin, qui
 * n'existe pas encore. Le rapport des deux est donc introuvable, et l'inventer serait exactement
 * l'interdit du §23.0 sur l'écran où l'on décide d'embaucher.
 * ⚠️ LA CARTE EST DESSINÉE QUAND MÊME, à l'anatomie de la maquette, et elle DIT ce qui lui manque :
 * la retirer ferait croire que l'indicateur n'existe pas au programme, alors qu'il attend un capteur.
 * L'anneau reste donc VIDE — pas à zéro pour cent, qui affirmerait qu'aucun visiteur n'achète.
 * Le jour où un compteur remonte, seule la valeur change ; la carte, elle, est déjà à sa place.
 */
function Transformation({ tickets, passages, dernierComptage }: {
  tickets: number;
  passages: number | null;
  /** ⚠️ Nul = ce magasin n'a JAMAIS compté. Renseigné sans passages sur la période = il a
   *  compté, puis s'est tu — deux phrases différentes, et c'était la même avant. */
  dernierComptage: number | null;
}) {
  // ⚠️ LE TAUX N'EXISTE QUE SI LES DEUX TERMES EXISTENT. Sans compteur, `passages` vaut `null` — et
  //    non zéro : un magasin ouvert où personne ne serait entré n'est pas la même chose qu'un magasin
  //    qu'on ne compte pas. Un dénominateur nul rendrait l'infini, ce qui s'afficherait fort bien.
  const mesure = passages !== null && passages > 0;
  const bp = mesure ? Math.round((tickets * 10_000) / passages!) : 0;
  return (
    <div className="dcard" style={{
      padding: 18, height: '100%', boxSizing: 'border-box',
      display: 'flex', alignItems: 'center', gap: 16,
    }}>
      {/* ⚠️ `valueBp={0}` ne dessine AUCUN arc : c'est la piste seule, donc « rien à montrer » — et non
          « 0 % », qui se lirait comme un magasin où personne n'achète. Le nom accessible le dit aussi,
          sans quoi un lecteur d'écran annoncerait « 0 % », c'est-à-dire une mesure. */}
      {/* ⚠️ TON `unmeasured` IMPOSÉ, ET SES PALIERS DÉCLARÉS QUAND MÊME. Rien n'est mesuré ici — le
          taux demande un compteur de passages qui n'existe pas — donc l'anneau reste gris et l'écran
          le dit en toutes lettres à côté. Les bandes sont posées dès maintenant pour que le jour où
          la mesure arrive, il suffise de retirer `tone` : la couleur suivra la règle métier au lieu
          d'être réinventée dans l'urgence. */}
      <Gauge
        valueBp={bp} size={66} bands={CONVERSION_RATE_BANDS}
        // ⚠️ LE TON N'EST IMPOSÉ QUE TANT QU'ON NE MESURE PAS. Dès qu'un compteur remonte, on le
        //    laisse tomber et la couleur suit les paliers métier (`CONVERSION_RATE_BANDS`) — c'est
        //    exactement ce que ces bandes attendaient depuis qu'elles ont été posées.
        tone={mesure ? undefined : 'unmeasured'}
        label={mesure
          ? t('{v} des passages se transforment en vente', { v: pourcent(bp, 1) })
          : t('Taux de transformation non mesuré')}
      >
        <span style={{
          fontSize: 'var(--t-lead)', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
          color: mesure ? 'var(--ink-900)' : 'var(--ink-400)',
        }}>{mesure ? pourcent(bp, 0) : '—'}</span>
      </Gauge>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)', letterSpacing: '.09em',
          textTransform: 'uppercase', color: 'var(--ink-500)',
        }}>{t('Taux de transformation')}</div>
        <div style={{ fontSize: 'var(--t-lead)', color: 'var(--ink-600)', marginTop: 5, lineHeight: 1.45 }}>
          <Trans k='<1>{n}</1> ticket' vars={{ n: tickets }} parts={[(x) => <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--ink-900)' }}>{x}</span>]} /><br />
          {/* ⚠️ MÊME GABARIT QUE LE DESSIN — « N tickets / pour M passages » — avec le seul mot qui
              change quand il n'y a rien à compter. La phrase précédente (« pour un nombre de passages
              non compté ») disait la même chose en trois fois plus long : elle repassait à la ligne
              et déformait la tuile, qui n'est plus alors la même que ses voisines. Une absence se dit
              AUSSI COURT qu'une présence, sinon c'est la mise en page qui annonce le manque. */}
          {/* ⚠️ TROIS PHRASES, PAS DEUX (corrigé 2026-09-05, retour utilisateur : « il y a un mot pour
              dire qu'il n'y a pas de compteur alors qu'on a du comptage dans le passé »). Un magasin
              SANS compteur et un compteur QUI S'EST TU rendaient la même ligne — mesuré sur la bêta :
              40 175 relevés en base, plus rien depuis le 18 août, et l'écran annonçait « aucun passage
              compté ». C'est vrai de la période, et faux de ce que le lecteur en comprend (§23.0). */}
          {mesure
            ? <>{t('pour {n} passage', { n: passages! })}</>
            : dernierComptage !== null
              ? <>{t('compteur muet depuis le {v}', { v: dateCompact(dernierComptage) })}</>
              : <>{t('pour aucun passage compté')}</>}
        </div>
        {/* ⚠️ LA PASTILLE NE PARAÎT QUE SANS MESURE : elle dit ce qui manque. Gardée une fois le
            compteur branché, elle contredirait le chiffre affiché juste au-dessus. */}
        {!mesure && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 7, height: 19,
            padding: '0 7px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)',
            color: 'var(--ink-500)', fontSize: 'var(--t-sub)', fontWeight: 600, whiteSpace: 'nowrap',
          }}>
            <Icon name="info" size={11} stroke={2.2} />
            {/* ⚠️ LA PASTILLE SUIT LA LIGNE DU DESSUS, sinon elle la contredit : « en attente d'un
                compteur » sous « compteur muet depuis le 18/08 » annonce une absence là où il y a un
                silence. Ce n'est pas le même problème, et ce n'est pas la même personne qui le règle
                — l'un installe un capteur, l'autre va voir pourquoi il ne remonte plus. */}
            {dernierComptage !== null ? t('Le compteur ne remonte plus') : t('En attente d’un compteur d’entrée')}
          </div>
        )}
      </div>
    </div>
  );
}
